import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Loader2, Receipt, FileText, X, QrCode, Tag, CreditCard, CheckCircle2, CalendarClock, ArrowLeft, Printer, AlertCircle, PackageCheck } from 'lucide-react';
import { Modal } from '../Modal';
import { MoneyInput } from '../inputs/MoneyInput';
import { UpperInput } from '../inputs/UpperInput';
import { ModalSenhaGerencial } from '../orcamento/ModalSenhaGerencial';
import { BotaoAutorizacao } from '../orcamento/BotaoAutorizacao';
import { CobrancaPix } from './CobrancaPix';
import { CobrancaCartao, ImprimirViaCartao } from './CobrancaCartao';
import { SimulacaoParcelamentoWhatsApp, type ResultadoParcelamento, type EstadoParcelamento } from '../pagamento/SimulacaoParcelamentoWhatsApp';
import { useVersao, detalheVersao } from '../RodapeVersao';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { preAprovacaoCartaoSobrevive, ehCartao, cobrancaPagaParaLinha } from '../../lib/casamento-cartao';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../Toast';
import { useDialog } from '../DialogProvider';
import { formatMoeda, formatMoedaSemSimbolo, formatDataHoraSP, totalComDescontoPct, totalComCartaoParcial, calcularCreditoPrograma, basePixParaTotal, classificarPagamentoFechamento, acrescimoAcimaDoTeto, centavoNaPernaPix, descalibrarTotalCalibrado, TETO_ACRESCIMO_PCT, TOLERANCIA_CENTAVO_CALIBRACAO, calcularVencimentoCrediario, ancoraDoVencimento, formatDataSP, realocarValorDaLinha, editarDescontoDaLinha, editarDescontoPctDaLinha, pctDescontoDaLinha, tipoNotaInicial, nomeTipoNota, normalizarCarteira, carteiraPadrao, parseTetoUso, tetoUsoPrograma, casarPixRecebidos, dividirLinhaPorPixRecebido, deveDesfazerCentavoCalibrado, type CarteiraCredito } from '@fiodecobre/shared';
import { baixarLinhaPixAoRecebido } from '../../lib/linha-pix-recebido';
import type { PedidoCaixa, CaixaConta } from '../../modules/caixa/CaixaPainel';

interface FormaPagamento {
  id: number;
  nome: string;
  tipo: string;
  permite_desconto: boolean;
}

// Linha de Pix JA RECEBIDO ([DA-625]): forma, valor e caixa ficam travados. Ela nao e uma escolha
// do operador — e o registro de dinheiro que ja caiu na conta e esta amarrado ao pedido. Mexer nela
// soltava o casamento e a venda voltava a cobrar o que a loja ja tinha recebido.
const TITULO_LINHA_PIX_TRAVADA =
  'Pix já recebido e vinculado a este pedido — não dá pra alterar aqui. Para desfazer, use a Conciliação Bancária.';

// PROGRAMA DO PRESTADOR (DA-435): as somas CRUAS do pedido, vindas do `GET /caixa/pedidos/:id`.
// `base_cheio` = soma do preco de tabela; `base_pix` = soma do preco pix congelado no envio ao
// caixa. Nao vem a previa pronta de proposito — ela depende das linhas de pagamento, que o operador
// ainda vai mexer; a conta roda no front com a MESMA funcao pura que o fechamento usa.
interface ProgramaBase {
  base_cheio: number;
  base_pix: number;
  custo_operacao_pct: number;
}

// Item do pedido pro painel "Ver itens" (conferencia do que vai entregar/cortar). Mesma forma da
// `interface ItemPedido` de CaixaPainel.tsx (so os campos que este painel usa).
interface ItemPedidoVenda {
  id: number;
  produto_descricao: string | null;
  codigo_interno: string | null;
  // O `GET /api/caixa/pedidos/:id` JA seleciona `p.foto_url` — o front so descartava.
  foto_url: string | null;
  unidade_sigla: string | null;
  quantidade: string | number;
  preco_unitario: string | number;
  subtotal: string | number;
}

interface PagamentoLinha {
  id: string;
  forma_pagamento_id: number | '';
  valor: number;
  caixa_id: number | '';
  dias_vencimento?: number;
  card_bandeira?: string;
  card_cod_autoriz?: string;
  card_nsu?: string;
  card_parcelas?: number;
  conta_cartao?: string; // 'mp' | 'pagbank' — maquininha escolhida no orcamento (so referencia)
  recebido?: number;
  // Desconto dado pelo operador NESTA linha (so pix/dinheiro; cartao paga cheio — DA-076). Em R$.
  // O `valor` da linha ja e o LIQUIDO cobrado; abaixar o desconto abaixa o valor cobrado E o total
  // da venda juntos.
  desconto?: number;
  // Valor CHEIO de referencia da linha (a alocacao original, sem ajuste). Travado na 1a mexida do
  // operador (desconto ou edicao do valor); e o denominador do % e a base dos tetos (−5% / +10%).
  // undefined => ainda nao mexeram; a referencia e o proprio valor atual.
  valorBruto?: number;
  // Qual carteira esta linha de Credito da Loja consome (DA-436). Uma compra aceita um unico
  // credito, entao a escolha e' da VENDA — as linhas de credito andam juntas.
  credito_origem?: CarteiraCredito;
}

// Tipos da cotacao de parcelamento (ParcelaCalculada/ResultadoParcelamento/EstadoParcelamento) vem
// da peca UNICA de simulacao (../pagamento/SimulacaoParcelamentoWhatsApp) — mesma fonte do orcamento.

interface Props {
  pedido: PedidoCaixa;
  contas: CaixaConta[];
  onClose: () => void;
  onSuccess: (venda: { id: number; numero: string; tipo_documento: 'nfce' | 'nfe' | 'troca' | null; aguardando_nota?: boolean; recibo?: import('../trocas/ModalReciboTroca').ReciboTrocaInfo }) => void | Promise<void>;
  // Liberado como Venda CM (DA-415): NAO e venda — nao gera nota, CR nem comissao. Por isso tem
  // callback proprio em vez de reusar o `onSuccess`, que dispara a emissao fiscal.
  onVendaCm: (numero: string) => void;
}

// Sub-linha de desconto do operador numa linha de pix/dinheiro (DA-202 por linha). Dois modos de
// entrada, como no orcamento: "Desc R$" (MoneyInput, aplica no blur) e "Desc %" (percentual do BRUTO
// da linha = valor cobrado + desconto ja aplicado). O `%` guarda texto local pra digitar sem brigar
// com o controlado; sincroniza quando o desconto muda por fora (R$, senha, edicao do valor). Ambos
// chamam `onAplicar(reais)`, que abaixa o valor cobrado e o total da venda juntos (o pai decide senha).
/**
 * Total do ledger partido em REAIS + CENTAVOS, pra cada metade ter cor, tamanho e altura proprios
 * (reais amarelo 92px na baseline / centavos laranja 46px sobrescritos — ver `.fv-total-display`
 * e `.fv-total-big` no styles.css).
 *
 * Existe porque um `<input>` tem cor e fonte UNICAS: enquanto o total era so o MoneyInput nao havia
 * como pintar/dimensionar as duas metades. Quem edita (o caminho sem cartao) troca este texto pelo
 * input ao clicar.
 */
function TotalPartido({ valor }: { valor: number }) {
  const [reais, centavos] = formatMoedaSemSimbolo(valor, 2).split(',');
  return (
    <>
      <span className="fv-total-reais">{reais}</span>
      <span className="fv-total-centavos">,{centavos}</span>
    </>
  );
}

/**
 * Desconto da linha (R$ + %), INLINE no fim da propria linha de pagamento — mesma fonte e tamanho
 * dos campos Forma/Valor/Caixa destino. Devolve DUAS celulas da grade (fragment), entao quem chama
 * precisa reservar as duas colunas em `gridTemplateColumns`.
 *
 * Nao mostra mais o "cobra R$ X": o campo VALOR ao lado JA e esse numero (aplicarDescLinha grava
 * `valor = ref - desconto`), e lado a lado o texto so repetia.
 */
function DescontoLinha({ bruto, desconto, onAplicar, labelStyle }: {
  bruto: number; desconto: number; onAplicar: (reais: number) => void; labelStyle: React.CSSProperties;
}) {
  // % e R$ sao a MESMA coisa vista de dois jeitos — as duas contas saem da regra pura testada
  // (`packages/shared/src/caixa.ts`), pra o campo % e o campo R$ nunca discordarem entre si.
  const pct = pctDescontoDaLinha(bruto, desconto);
  const [pctText, setPctText] = useState('');
  // O texto que o campo recebeu do estado. Passar pelo campo sem digitar nada NAO pode mexer no
  // desconto: o exibido e o percentual ARREDONDADO a 2 casas, e reaplica-lo devolve outro numero —
  // R$ 10,00 num bruto de 605,01 vira 1,65% e volta como R$ 9,98 (floor). O operador dava desconto
  // pelo campo R$, tocava no % de passagem e perdia 2 centavos calado. (DA-582)
  const pctSincronizado = useRef('');
  useEffect(() => {
    const t = desconto > 0.005 ? pct.toFixed(2).replace('.', ',') : '';
    setPctText(t);
    pctSincronizado.current = t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desconto, bruto]);
  const aplicarPct = () => {
    if (pctText === pctSincronizado.current) return;
    const v = Math.max(0, parseFloat((pctText || '0').replace(',', '.')) || 0);
    onAplicar(editarDescontoPctDaLinha(bruto, v).desconto);
  };
  return (
    <>
      <div data-no-enter-tab="true">
        {/* Sem icone: com 50px de coluna ele empurrava o "Desc. R$" pra uma 2a linha. */}
        <label style={{ ...labelStyle, whiteSpace: 'nowrap' }}>Desc. R$</label>
        <MoneyInput
          className="cel-num"
          valor={desconto}
          onChangeValor={(v) => onAplicar(v)}
          style={{ width: '100%', textAlign: 'right', padding: '6px 8px', fontSize: 14, borderRadius: 4 }}
        />
      </div>
      <div data-no-enter-tab="true">
        <label style={{ ...labelStyle, whiteSpace: 'nowrap' }}>%</label>
        <input
          type="text"
          inputMode="decimal"
          className="cel-num"
          value={pctText}
          onChange={(e) => setPctText(e.target.value)}
          onBlur={aplicarPct}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aplicarPct(); } }}
          placeholder="0,00"
          style={{ width: '100%', textAlign: 'right', padding: '6px 8px', fontSize: 14, borderRadius: 4 }}
        />
      </div>
    </>
  );
}

export function ModalFinalizarVenda({ pedido, contas, onClose, onSuccess, onVendaCm }: Props) {
  const toast = useToast();
  const dialog = useDialog();
  const { usuario } = useAuth();
  // O modal cobre o rodape da pagina — carimba a versao aqui pra saber qual release esta na tela.
  const versao = useVersao();
  // Total parado = texto colorido; clicou = MoneyInput com foco. Ver TotalPartido.
  const [editandoTotal, setEditandoTotal] = useState(false);
  // Itens da venda (conferencia do que vai entregar/cortar — cabo/mangueira/cano so' se cortam
  // depois do pagamento). null = ainda nao buscou. Desde 08/10/2026 vem ao abrir e fica a vista no
  // ledger; o botao "Ver itens" + painel que cobria o total SAIRAM no mesmo dia (pedido dele).
  const [itens, setItens] = useState<ItemPedidoVenda[] | null>(null);
  const [itensCarregando, setItensCarregando] = useState(false);
  // Grid navegavel dos itens: clicar marca de amarelo, ↑/↓ navegam. Sempre ativo — a lista esta
  // sempre a vista.
  const gridItens = useGridNavegavel<ItemPedidoVenda>({
    itens: itens ?? [],
    getId: (it) => it.id,
    ativarPorEnter: false,
    ativo: true,
  });
  // Cobrança no CARTÃO via maquininha Point (modo PDV) pela peça <CobrancaCartao>, por linha. O pai só
  // guarda 2 flags por linha: ATIVO (cobrança rolando → trava o caixa no MP) e APROVADO (a maquininha
  // aprovou → libera a finalização da nota). A peça conduz o ciclo e avisa pelos callbacks.
  const [cartaoAtivoLinha, setCartaoAtivoLinha] = useState<Record<string, boolean>>({});
  // Maquininha imprime a via do cliente? Desmarcado = sem papel; marca só quando o cliente pede.
  const [imprimirViaCartao, setImprimirViaCartao] = useState(false);
  const [cartaoAprovadoLinha, setCartaoAprovadoLinha] = useState<Record<string, boolean>>({});
  // Cobranca de cartao JA PAGA detectada ao abrir (finalizacao barrou depois de cobrar, ou modal
  // reaberto): o pai marca a linha como aprovada SEM montar a <CobrancaCartao> (que recobraria).
  // Guarda o dado do recebimento ja pago pra pintar o banner "APROVADO" e o backend le a cobranca
  // paga por orcamento_id no finalize (usaPoint) — finaliza sem cobrar de novo.
  // `ajustadaDe` = valor que a linha PEDIA antes de cair pro valor realmente pago (cobertura parcial,
  // ver o casamento abaixo). null quando a cobranca bateu a linha em cheio.
  const [cartaoJaPagoLinha, setCartaoJaPagoLinha] = useState<Record<string, { valor: number; bandeira: string | null; parcelas: number | null; cobrancaId: number | null; reaproveitar: boolean; ajustadaDe: number | null }>>({});
  // [DA-775] Cobranças de cartão pagas que esta tela ja CONTOU — as casadas ao abrir (acima) mais as
  // aprovadas ao vivo nesta sessao. Vai em todo "Cobrar na maquininha": o backend recusa se o pedido
  // tiver paga fora desta lista (aprovacao que chegou com a tela ja aberta = cobranca dupla).
  const cartaoPagasSessaoRef = useRef<Set<number>>(new Set());
  // Cobrancas de cartao pagas deste pedido, como vieram ao abrir — inclusive as que NAO casaram com
  // nenhuma linha (pedido salvo em Pix e pago na maquininha). Quando o operador troca uma linha pra
  // cartao, o casamento se refaz contra esta lista (`cobrancaPagaParaLinha`, ORC-008563).
  const [cartoesPagosPedido, setCartoesPagosPedido] = useState<Array<{ valor_alocado: string; valor_recebimento: string; bandeira: string | null; parcelas: number | string | null; cobranca_id: number | string | null; status: string }>>([]);
  const cartaoPagasConhecidas = [
    ...Object.values(cartaoJaPagoLinha).map((j) => j.cobrancaId).filter((x): x is number => x != null),
    ...cartaoPagasSessaoRef.current,
  ];
  // Pix JA RECEBIDO e vinculado a este pedido (mp_vinculos -> mp_recebimentos). Mesma historia do
  // cartao logo acima, que o Pix nao tinha: o modal reabre sobre uma cobranca ja paga (a venda barrou,
  // o operador fechou a tela) e a peca do Pix so sabe CRIAR cobranca — nao reidrata a existente. Sem
  // isso a tela oferece "Cobrar Pix" de novo, nasce um 2o QR na tela do cliente e ele pode pagar 2x
  // (o POST /api/pix/cobrar nao barra cobranca ja paga do mesmo pedido). (DA-322)
  const [pixJaPagoLinha, setPixJaPagoLinha] = useState<Record<string, { valor: number; pagador: string | null }>>({});
  const [cartaoMaquininha, setCartaoMaquininha] = useState(false);
    // Dinheiro de operadora comum (nao admin/gerente) cai SEMPRE na gaveta pessoal dela; "Movimento
  // de Caixa" so aparece pra admin/gerente. O backend e quem garante isso (e bloqueia se a gaveta
  // estiver fechada) — aqui so refletimos no select pra nao confundir.
  const ehAdminGerente = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
  // "Ajuda no caixa" (vende_sem_turno, DA-384): finaliza como as vendedoras, mas o dinheiro dela
  // cai no Movimento de Caixa (sem gaveta pessoal nem turno) — igual admin/gerente no roteamento
  // do dinheiro. `roteiaLivre` unifica os dois casos que NAO usam gaveta pessoal.
  const vendeSemTurno = usuario?.vende_sem_turno === true;
  const roteiaLivre = ehAdminGerente || vendeSemTurno;
  // Venda CM (DA-415, cartao RDS 12.4): o selo do cadastro HABILITA, nao determina — o mesmo
  // cliente de obra as vezes paga a vista. Por isso o botao apenas APARECE; os dois de nota
  // seguem intactos ao lado dele. Gate ADMIN estrito de proposito: `ehAdminGerente` acima inclui
  // gerente e NAO serve aqui (a regra do Ruan e admin). Backend repete a trava (403).
  const podeVendaCm = usuario?.perfil === 'admin' && pedido.cliente_consignacao === true;
  const [modalVendaCm, setModalVendaCm] = useState(false);
  const [liberandoVendaCm, setLiberandoVendaCm] = useState(false);
  // "Retirado por" mora aqui, e nao so no orcamento: o material sai sem nota e quem assina o
  // comprovante esta NO BALCAO, na hora. Vindo vazio do pedido, o campo e digitado na confirmacao
  // — sem ele a Autorizacao se recusa a imprimir.
  const [retiradoPor, setRetiradoPor] = useState(pedido.comprador ?? '');
  const [meuCaixa, setMeuCaixa] = useState<{ id: number; nome: string } | null>(null);
  const [formas, setFormas] = useState<FormaPagamento[]>([]);
  const [pagamentos, setPagamentos] = useState<PagamentoLinha[]>([]);
  const [cpfNota, setCpfNota] = useState<string>(pedido.cpf_nota ?? '');
  // Botão "Cobrar Pix na tela do cliente": só aparece quando o provedor ativo é o GRATUITO
  // (QR estático custo-zero, dirigido pela env PIX_CHAVE_ESTATICA na VPS — liga sem redeploy).
  // Provedores pagos (Cora/MP) ficam ocultos de propósito (`gratuito=false`).
  const [pixCobrancaAtiva, setPixCobrancaAtiva] = useState(false);
  const [enviando, setEnviando] = useState<'nfce' | 'nfe' | 'troca' | null>(null);
  // Saldo de credito do cliente SEPARADO por carteira (DA-436). Uma compra aceita um unico
  // credito — usou o de troca, nao aceita o do programa e vice-versa —, entao mostrar so a soma
  // esconderia do operador a escolha que a tela esta pedindo que ele faca.
  const [saldos, setSaldos] = useState<{ troca: number; programa: number }>({ troca: 0, programa: 0 });
  // Teto de uso do credito do PROGRAMA (% da compra). null = sem limite. Nao vale pro credito de
  // troca, que e' dinheiro do proprio cliente voltando.
  const [tetoUsoPct, setTetoUsoPct] = useState<number | null>(null);
  // PROGRAMA DO PRESTADOR (DA-435): as duas somas cruas + a aliquota, vindas do servidor. A conta
  // em si roda nas funcoes puras compartilhadas, pra a previa e o fechamento nunca divergirem.
  const [programaBase, setProgramaBase] = useState<ProgramaBase | null>(null);
  // Caixa pre-configurado em /configuracoes/recebimentos. Fallback pro Banco
  // do Brasil mantem compat enquanto a config nao for definida.
  const [caixaPreferidoId, setCaixaPreferidoId] = useState<number | null>(null);
  // Cotacao de parcelamento por linha de cartao (chave = id da linha). So EXIBE.
  const [parcelamentos, setParcelamentos] = useState<Record<string, EstadoParcelamento>>({});
  // Provedor que respondeu a cotacao daquela linha — deriva de qual endpoint foi chamado
  // (/api/cartao/parcelamento = maquininha Point do Mercado Pago; /api/caixa/parcelamento = PagBank).
  // So pra rotular a "Simulacao por WhatsApp"; nao influencia nenhum calculo.
  const [parcelamentoProvedor, setParcelamentoProvedor] = useState<Record<string, 'Mercado Pago' | 'PagBank'>>({});

  // Cobrança Pix por LINHA via a peça única <CobrancaPix> (DA-237 Fase 2). Cada linha de Pix decide
  // QUANDO cobrar: o botão "Cobrar Pix" liga `pixAtivoLinha[id]` → o componente monta, COBRA AO MONTAR
  // (rota /api/pix/cobrar, amarrada ao orçamento) e conduz sozinho — QR na tela do cliente, poll/socket,
  // confirma sozinho (número 62-05 ou valor quebrado único) ou pede o pagador, comprovante, e cancela
  // no DESMONTE se ainda pendente. O "pago" só marca estado visual — NÃO finaliza a venda (ela fecha
  // pelos botões Nota Consumidor/A4, comportamento intacto). Trocar valor/forma ou remover a linha
  // desliga o flag → o componente desmonta e cancela o QR no backend. Terminal ocupado por OUTRA linha
  // não é mais barrado preventivamente — o backend devolve 409 ao montar e a peça reverte sozinha.
  const [pixAtivoLinha, setPixAtivoLinha] = useState<Record<string, boolean>>({});
  const desligarPixLinha = (lineId: string) =>
    setPixAtivoLinha((m) => (m[lineId] ? { ...m, [lineId]: false } : m));
  // Pix que caiu AGORA, nesta sessao do modal (a peca avisa pelo onPago — que ate a DA-403 este
  // modal nao escutava). Irmao do `pixJaPagoLinha`, que so cobre o Pix que JA estava vinculado na
  // abertura. Sem os dois, "a linha existe" era confundido com "o dinheiro entrou".
  const [pixPagoNaSessao, setPixPagoNaSessao] = useState<Record<string, boolean>>({});
  // [DA-628] O centavo que o backend acrescentou ao gerar um QR PARCIAL, guardado por linha pra poder
  // ser DESFEITO. Sem isto o QR cancelado sem pagamento deixaria o centavo orfao no total e a gaveta
  // voltaria a pedir um valor impossivel de contar em especie — o defeito da [DA-346] entrando pela
  // porta nova. Guarda o `totalAlvo` de ANTES (devolver zerando apagaria um desconto anterior; e
  // deixar em 0 em vez de null manteria a descalibracao desligada) e o de DEPOIS, que e como se sabe
  // se o operador mexeu no total nesse meio tempo — se mexeu, so o delta e' descontado.
  const [ajusteQrPorLinha, setAjusteQrPorLinha] = useState<Record<string, {
    valorAntes: number; delta: number; totalAlvoAntes: number | null; totalAlvoDepois: number;
  }>>({});
  // `restaurarValor` distingue as duas portas por onde um QR morre: CANCELADO devolve a linha ao valor
  // pedido (o centavo era dele); linha REESCRITA pelo operador fica com o que ele digitou — so o total
  // volta. Linha ja paga nunca desfaz: ali o centavo entrou de verdade na conta da loja.
  function desfazerAjusteQr(lineId: string, restaurarValor: boolean) {
    const aj = ajusteQrPorLinha[lineId];
    if (!aj) return;
    // ⛔ Pix PAGO nunca desfaz: aquele centavo entrou de verdade na conta da loja, e baixar o total
    // faria a venda declarar MENOS do que recebeu. A guarda mora aqui, e nao em cada chamada — hoje a
    // linha confirmada e' travada na tela ([DA-625]), entao este caminho e' inalcancavel pelo uso
    // normal; amanha alguem destrava e a protecao tem que continuar de pe.
    if (pixPagoNaSessao[lineId] || pixJaPagoLinha[lineId]) return;
    if (restaurarValor) {
      setPagamentos((ps) => ps.map((x) => (x.id === lineId ? { ...x, valor: aj.valorAntes } : x)));
    }
    setTotalAlvo((atual) => (
      atual != null && Math.abs(atual - aj.totalAlvoDepois) < 0.005
        ? aj.totalAlvoAntes
        : atual == null ? null : Math.round((atual - aj.delta) * 100) / 100
    ));
    setAjusteQrPorLinha((m) => { const n = { ...m }; delete n[lineId]; return n; });
  }

  // Desconto dado/retirado pelo operador na propria tela de fechamento (DA-202). Evita
  // devolver a venda pro vendedor so pra ajustar preco — preserva o vendedor original (e a
  // comissao) e as caracteristicas da venda. O preco CHEIO (cartao) e o subtotal_bruto;
  // o desconto leva o total ao pix. So aplica em pix/dinheiro/crediario — cartao paga cheio.
  const totalOriginalPedido = Number(pedido.total);
  const cheio = Math.max(Number(pedido.subtotal_bruto) || 0, totalOriginalPedido);
  // Dinheiro tem 2 casas: so existe desconto quando a diferenca chega a UM CENTAVO. O epsilon de
  // meio centavo que havia aqui (`cheio > x + 0.005`) deixava residuo de arredondamento passar por
  // desconto — pedido SEM desconto nenhum abria o aviso "Cartao paga o preco cheio" (DA-418). A
  // causa foi corrigida na origem (o backend soma o cheio arredondando por linha); esta regua de
  // centavo e a rede embaixo, pra a fracao de centavo nunca mais virar regra de negocio.
  const emCentavos = (v: number) => Math.round(v * 100);
  const temDescontoDeTabela = emCentavos(cheio) > emCentavos(totalOriginalPedido);
  const pctInicial = cheio > 0 ? Math.round((1 - totalOriginalPedido / cheio) * 10000) / 100 : 0;
  const fmtPct = (n: number) => n.toFixed(2).replace('.', ','); // pt-BR: virgula decimal
  // O desconto pode ser ajustado de 2 jeitos (como no orcamento): digitando o % OU editando
  // o Total direto. A fonte da verdade e o `totalAlvo` (total exato); o % e derivado dele.
  const [totalAlvo, setTotalAlvo] = useState<number | null>(null);
  const [descontoInput, setDescontoInput] = useState(pctInicial > 0 ? fmtPct(pctInicial) : '');
  const [senhaGerencial, setSenhaGerencial] = useState<string | null>(null);
  const [showSenhaDesc, setShowSenhaDesc] = useState(false);
  const [pendenteAplicar, setPendenteAplicar] = useState<number | null>(null); // total aguardando senha
  // Desconto por linha (pix/dinheiro) aguardando senha gerencial (passou de 5% da venda).
  const [pendenteDescLinha, setPendenteDescLinha] = useState<{ idx: number; desconto: number } | null>(null);
  // Marca que o desconto de pix foi removido AUTOMATICAMENTE por ter entrado cartao (DA-076).
  // Alimenta o aviso e permite devolver o desconto se o cartao for retirado.
  const [descontoRemovidoPorCartao, setDescontoRemovidoPorCartao] = useState(false);

  // Base no NIVEL PIX (com desconto): o total exato do pedido ou o alvo que o operador ajustou.
  // O % efetivo do desconto deriva dele sobre o cheio — alimenta o controle e o payload.
  const descontoAlterado = totalAlvo != null;
  // Descalibracao ao vivo (ponta solta #1 do caixa): pedido calibrado (requer_valor_unico) tem o total
  // congelado com centavo != 0 pro Pix de fora casar sozinho. Se a venda fecha SEM Pix nas formas
  // (dinheiro/cartao/crediario), esse centavo vira orfao — a gaveta esperaria um valor impossivel de
  // contar em especie. No instante em que o operador tira o Pix da mistura, o total gigante cai pro
  // NATURAL (descalibrarTotalCalibrado desfaz so o bump ate a grade). O backend (rede) reprecifica pro
  // mesmo valor na finalizacao. Quando o operador ajusta desconto/total (totalAlvo != null) o proprio
  // caminho de desconto ja produz redondo sem Pix — entao aqui so age quando ninguem mexeu no total.
  // ⚠️ [DA-626] A pergunta NAO e "tem Pix nas formas?", e "ainda ha Pix a COBRAR?". O centavo e uma
  // marca pro Pix que vai CHEGAR; com todo o Pix ja caido e vinculado ele nao identifica mais nada e
  // vira um valor que o cliente nao consegue pagar — o pedido pede 102,01, ele entrega 102,00, e a
  // trava de gravacao ([DA-531], igualdade exata) recusa a venda depois do clique. Sem linha de Pix
  // nenhuma a conta da 0 e o comportamento antigo fica intacto.
  // ⚠️ [DA-629] "Nao resta Pix a cobrar" abracava dois casos OPOSTOS: o parcial pago (o da DA-626,
  // onde o centavo fica orfao no que ainda falta receber) e o INTEGRAL pago no valor calibrado, onde
  // o centavo ja entrou na conta da loja. No segundo, baixar o total deixava a linha confirmada —
  // fixa desde a DA-626 p.8 — sobrando 1 centavo, e a mesma trava recusava a venda. A segunda
  // condicao mora no `deveDesfazerCentavoCalibrado`.
  const pixDeclaradoLinhas = pagamentos.reduce((s, p) => (
    getFp(p.forma_pagamento_id)?.tipo === 'pix' ? s + Number(p.valor || 0) : s
  ), 0);
  const pixConfirmadoLinhas = pagamentos.reduce((s, p) => {
    if (getFp(p.forma_pagamento_id)?.tipo !== 'pix') return s;
    if (pixJaPagoLinha[p.id]) return s + Number(pixJaPagoLinha[p.id]!.valor || 0);
    if (pixPagoNaSessao[p.id]) return s + Number(p.valor || 0);
    return s;
  }, 0);
  // [DA-807] Cartao ja aprovado na maquininha (agora ou antes de reabrir) segura o centavo, como o
  // Pix ja recebido — mesma conta do backend, senao a tela baixaria a linha do cartao pra 89,00.
  const cartaoAprovadoLinhas = pagamentos.reduce((s, p) => {
    const t = getFp(p.forma_pagamento_id)?.tipo;
    if (t !== 'cartao_credito' && t !== 'cartao_debito') return s;
    return cartaoAprovadoLinha[p.id] ? s + Number(p.valor || 0) : s;
  }, 0);
  const deveDescalibrar = pedido.requer_valor_unico && totalAlvo == null
    && deveDesfazerCentavoCalibrado(totalOriginalPedido, pixDeclaradoLinhas, pixConfirmadoLinhas, cartaoAprovadoLinhas);
  const baseSemAlvo = deveDescalibrar
    ? descalibrarTotalCalibrado(totalOriginalPedido)
    : totalOriginalPedido;
  const basePix = totalAlvo ?? baseSemAlvo;
  const descontoEfetivoPct = cheio > 0 ? Math.max(0, Math.round((1 - basePix / cheio) * 10000) / 100) : 0;
  // Cartao paga cheio na SUA fatia (DA-076). `valorCartaoLinhas` = quanto sera cobrado no cartao (cheio);
  // `temCartao` desabilita o controle de desconto (a regra e do cartao). getFp e hoisted (function).
  const valorCartaoLinhas = pagamentos.reduce((s, p) => {
    const t = getFp(p.forma_pagamento_id)?.tipo;
    return (t === 'cartao_credito' || t === 'cartao_debito') ? s + Number(p.valor || 0) : s;
  }, 0);
  const temCartao = pagamentos.some((p) => {
    const t = getFp(p.forma_pagamento_id)?.tipo;
    return t === 'cartao_credito' || t === 'cartao_debito';
  });
  // Total BASE da venda (antes do desconto por linha): cartao paga cheio na sua fatia, o resto mantem
  // o desconto (refino DA-274). Regra unica compartilhada com o backend. Sem cartao = basePix (intacto).
  const totalBase = temCartao ? totalComCartaoParcial(cheio, basePix, valorCartaoLinhas) : basePix;

  // Desconto por linha dado pelo operador no fechamento (pix/dinheiro apenas — cartao paga cheio).
  // Cada real de desconto abaixa o total da venda E o valor cobrado da linha juntos (o `valor` da
  // linha ja e o liquido). Some do total; o "Restante" continua zerado (e desconto, nao falta).
  const descontoLinhas = +pagamentos.reduce((s, p) => {
    const t = getFp(p.forma_pagamento_id)?.tipo;
    return (t === 'pix' || t === 'dinheiro') ? s + Number(p.desconto || 0) : s;
  }, 0).toFixed(2);
  // Total EFETIVO a cobrar: base menos o desconto por linha. E o valor exibido, o alvo do "Restante"
  // e a base da soma de pagamentos (cada linha ja carrega o liquido, entao soma == total).
  const totalPedido = +(totalBase - descontoLinhas).toFixed(2);

  // PROGRAMA DO PRESTADOR (DA-435) — a previa do premio, viva conforme o operador mexe nas linhas.
  //
  // A base e' quanto o material custaria PELA FORMA ESCOLHIDA, sem o markup: a fatia no cartao puxa
  // o preco pro cheio, o resto fica no preco pix congelado. E' a mesma funcao pura que o backend
  // chama no fechamento — o numero da tela e o numero gravado nascem da mesma conta, de proposito.
  // O valor informado e' o total do pedido: a vendedora ja o digitou no Liquido do orcamento e os
  // itens ja subiram proporcionalmente; aqui so descobrimos quanto daquilo era markup.
  const previaPrograma = useMemo(() => {
    if (!programaBase || programaBase.base_cheio <= 0) return null;
    const base = totalComCartaoParcial(programaBase.base_cheio, programaBase.base_pix, valorCartaoLinhas);
    return calcularCreditoPrograma(base, totalPedido, programaBase.custo_operacao_pct);
  }, [programaBase, valorCartaoLinhas, totalPedido]);

  const somaPagamentos = useMemo(
    () => pagamentos.reduce((s, p) => s + Number(p.valor || 0), 0),
    [pagamentos],
  );
  const diferenca = +(totalPedido - somaPagamentos).toFixed(2);
  // FALTA no lugar do TOTAL no numero gigante (DA-561). Parte do dinheiro ja esta coberta — o Pix
  // que caiu antes de abrir a tela, uma forma ja lancada — e o hero seguia exibindo o preco cheio:
  // o operador lia 375,01 num pedido em que so restam 372,02 a cobrar. Inverte so quando ha os DOIS
  // numeros pra mostrar: sem nada coberto (`soma` zero, caso de toda venda que abre limpa) o hero
  // segue sendo o Total, senao "falta 375,01 de R$ 375,01" seria a mesma coisa dita duas vezes.
  const heroFaltaAtivo = somaPagamentos > 0.005 && diferenca > 0.005;
  // Editando o total, o hero vira o campo de digitacao e volta a falar do preco cheio — entao tudo
  // que depende de "o numero gigante esta dizendo FALTA" pergunta por esta, nao pela de cima.
  const heroMostraFalta = heroFaltaAtivo && !editandoTotal;
  // Descalibracao ao vivo (ponta #1): quando o Pix sai da mistura de um pedido calibrado, o total cai
  // pro natural, mas as linhas foram pre-preenchidas no valor CALIBRADO (com o centavo do bump), entao
  // a soma sobra 1 centavo. Reencaixa a diferenca — mas SO quando a sobra e exatamente o bump (<=
  // tolerancia): se o operador montou um split proprio ou esta pagando a mais de verdade (acrescimo),
  // a sobra passa da tolerancia e nao mexemos. Converge (apos o refit a sobra zera e o efeito no-opa).
  useEffect(() => {
    if (!deveDescalibrar) return;
    const alvo = descalibrarTotalCalibrado(totalOriginalPedido);
    const sobra = +(somaPagamentos - alvo).toFixed(2);
    if (!(sobra > 0.0001 && sobra <= TOLERANCIA_CENTAVO_CALIBRACAO)) return;
    // O refit nao toca linha de Pix ja recebido ([DA-626]). Se ELA e' que carrega o centavo (pedido
    // recalibrado depois do Pix cair — ORC-008624), baixa pro que de fato caiu; senao, refit de sempre.
    const baixa = baixarLinhaPixAoRecebido(
      pagamentos.map((p) => ({
        id: p.id,
        valor: Number(p.valor || 0),
        recebido: getFp(p.forma_pagamento_id)?.tipo === 'pix' && pixJaPagoLinha[p.id]
          ? Number(pixJaPagoLinha[p.id]!.valor || 0) : null,
      })),
      sobra,
    );
    if (baixa) {
      setPagamentos((prev) => prev.map((p) => (p.id === baixa.id ? { ...p, valor: baixa.valor } : p)));
      return;
    }
    refitPagamentos(alvo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deveDescalibrar, somaPagamentos, totalOriginalPedido]);
  // Acrescimo no fechamento (gemeo do desconto DA-202): cobrar ACIMA do total ate 10% e aceito
  // como preco real (a operadora ajustou pra cima / arredondou). A regra unica compartilhada
  // classifica sobre o total ja computado. `acrescimo` = quanto passou do total (positivo).
  const classePag = classificarPagamentoFechamento(totalPedido, somaPagamentos);
  const acrescimo = +(somaPagamentos - totalPedido).toFixed(2);

  // Credito da Loja vira vDesc na nota (DA-049): a nota sai por total - credito. Exibido
  // como subtitulo do Total pra ninguem achar que a nota vai sair pelo valor cheio.
  const creditoLojaTotal = useMemo(
    () => pagamentos.reduce((s, p) => {
      const fp = formas.find((f) => f.id === p.forma_pagamento_id);
      return fp?.tipo === 'credito_loja' ? s + Number(p.valor || 0) : s;
    }, 0),
    [pagamentos, formas],
  );
  const valorNota = +(totalPedido - creditoLojaTotal).toFixed(2);

  // Quanto da venda sai FIADO. As linhas cobrirem o total nao quer dizer que a loja recebeu: o
  // crediario nao e dinheiro que entra, e divida que nasce (vira CR ao finalizar). Sem isto o
  // ledger cantava "QUITADO" justamente na venda em que ninguem pagou nada.
  //
  // Credito da Loja NAO entra aqui de proposito — ali o valor ja e da loja (o cliente adiantou),
  // entao aquilo esta liquidado de verdade.
  const crediarioTotal = useMemo(
    () => +pagamentos.reduce((s, p) => {
      const fp = formas.find((f) => f.id === p.forma_pagamento_id);
      return fp?.tipo === 'crediario' ? s + Number(p.valor || 0) : s;
    }, 0).toFixed(2),
    [pagamentos, formas],
  );


  // A tarja "PEDIDO QUITADO" fala do pagamento que RESOLVEU a diferenca do cartao — entao ela mora
  // embaixo DAQUELA linha (o dinheiro/Pix que cobriu), no mesmo molde do selo de aprovacao, que fica
  // colado no cartao. Ate 30/07 ela renderizava sob a linha do CARTAO, falando de um pagamento que
  // estava duas linhas abaixo. So desconto (sem linha nova pra cobrir) volta pra linha do cartao,
  // que e onde a diferenca nasceu.
  const tarjaDiferenca = (() => {
    const linhaCartao = pagamentos.find(
      (p) => cartaoJaPagoLinha[p.id]?.ajustadaDe != null && cartaoAprovadoLinha[p.id] && !cartaoAtivoLinha[p.id],
    );
    if (!linhaCartao) return null;
    const r = resolucaoDaDiferenca(linhaCartao, cartaoJaPagoLinha[linhaCartao.id]!.ajustadaDe!);
    if (!r.coberto) return null;
    const outras = pagamentos.filter((p) => p.id !== linhaCartao.id && Number(p.valor || 0) > 0.005);
    const ultima = outras.at(-1);
    return { host: ultima ? ultima.id : linhaCartao.id, gap: r.gap, partes: r.partes };
  })();

  // DA-140 (exibicao): acrescimo financeiro (juros+imposto) das linhas de cartao de credito
  // parceladas — MESMA conta do submit (sel.total - tabela). Some no "Total a cobrar" exibido pra
  // a operadora ver o valor REAL cobrado na maquininha / que a nota vai carregar ANTES de finalizar.
  // Nao mexe em venda/comissao (essas seguem no totalPedido/pix); so expoe o que ja e cobrado.
  const acrescimoCartao = +pagamentos.reduce((acc, p) => {
    if (getFp(p.forma_pagamento_id)?.tipo !== 'cartao_credito') return acc;
    const est = parcelamentos[p.id];
    const sel = est?.data?.parcelas.find((x) => x.n === (p.card_parcelas ?? 1));
    if (!sel || !est?.data) return acc;
    const a = (sel.total - est.data.tabela) / 100;
    return a > 0 ? acc + a : acc;
  }, 0).toFixed(2);
  const totalACobrar = +(totalPedido + acrescimoCartao).toFixed(2);

  // Razao cheio/liquido do pedido (preco de tabela / total liquido). Converte o
  // valor de uma linha de cartao no preco cheio dela; o piso pix e derivado no
  // backend. Fallback 1 quando nao da pra inferir (sem markup extra de tabela).
  const ratioCheio = useMemo(() => {
    const sb = Number(pedido.subtotal_bruto || 0);
    const tot = Number(pedido.total || 0);
    return sb > 0 && tot > 0 && sb >= tot ? sb / tot : 1;
  }, [pedido.subtotal_bruto, pedido.total]);

  // Assinatura (id:centavos:caixa) das linhas de cartao de credito — dispara a cotacao
  // viva so quando muda a linha, o valor OU o caixa destino (a adquirente que cota: Mercado
  // Pago vs PagBank). Debitos sao a vista (sem parcela).
  const cartaoSig = useMemo(
    () => pagamentos
      .filter((p) => formas.find((f) => f.id === p.forma_pagamento_id)?.tipo === 'cartao_credito')
      .map((p) => `${p.id}:${Math.round(Number(p.valor || 0) * 100)}:${p.caixa_id}`)
      .join('|'),
    [pagamentos, formas],
  );

  useEffect(() => {
    const cartoes = pagamentos.filter(
      (p) => formas.find((f) => f.id === p.forma_pagamento_id)?.tipo === 'cartao_credito',
    );
    if (cartoes.length === 0) return;
    // Caixa Mercado Pago = maquininha Point (cota no MP); qualquer outro caixa (PagBank,
    // Infinitepay, BB...) cota na PagBank. A simulacao segue o caixa destino da LINHA, igual
    // a conta escolhida no orcamento — mesma regra/imposto, so a taxa de juros muda.
    const mpCaixaId = contas.find((c) => /mercado\s*pago/i.test(c.nome))?.id ?? null;
    let cancelado = false;
    const timer = setTimeout(() => {
      cartoes.forEach(async (p) => {
        const liquidoC = Math.round(Number(p.valor || 0) * 100);
        if (liquidoC <= 0) return;
        const cheioC = Math.round(Number(p.valor || 0) * ratioCheio * 100);
        setParcelamentos((m) => ({ ...m, [p.id]: { ...m[p.id], loading: true, erro: undefined } }));
        try {
          const usaPoint = cartaoMaquininha && mpCaixaId != null && p.caixa_id === mpCaixaId;
          const base = usaPoint ? '/api/cartao/parcelamento' : '/api/caixa/parcelamento';
          const r = await api.get<ResultadoParcelamento>(
            `${base}?liquido=${liquidoC}&cheio=${cheioC}`,
          );
          if (!cancelado) {
            setParcelamentos((m) => ({ ...m, [p.id]: { loading: false, data: r } }));
            setParcelamentoProvedor((m) => ({ ...m, [p.id]: usaPoint ? 'Mercado Pago' : 'PagBank' }));
          }
        } catch (e: any) {
          if (!cancelado) setParcelamentos((m) => ({ ...m, [p.id]: { loading: false, erro: e.message } }));
        }
      });
    }, 400);
    return () => { cancelado = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartaoSig, ratioCheio, cartaoMaquininha]);

  // NF-e (A4) exige cliente cadastrado (cliente_id !== 1 = Consumidor generico)
  const nfeIndisponivel = pedido.cliente_id === 1;
  // DA-877: o tipo marcado pela vendedora e' o PADRAO, o caixa troca na caixa de escolha
  // "Tipo de Nota" (longe do botao de finalizar — o clique no botao gemeo era o erro da DA-432).
  // A trava do cadastro do cliente (A4) continua: ali a caixa fica presa.
  const [tipoNota, setTipoNota] = useState<'nfce' | 'nfe'>(
    () => tipoNotaInicial(pedido.tipo_documento, pedido.cliente_travar_nota_a4, pedido.cliente_id === 1),
  );

  useEffect(() => {
    let cancelado = false;
    async function carregar() {
      try {
        const podeConsultarCredito = pedido.cliente_id && pedido.cliente_id !== 1;
        const [det, fps, saldoResp, cfgCaixa, gavetaPessoal, cfgTetoUso] = await Promise.all([
          api.get<{ pagamentos: any[]; programa?: ProgramaBase | null }>(`/api/caixa/pedidos/${pedido.id}`),
          api.get<FormaPagamento[]>('/api/formas-pagamento'),
          podeConsultarCredito
            ? api.get<{ saldo_troca: number | string; saldo_programa: number | string }>(`/api/creditos/cliente/${pedido.cliente_id}/saldo`)
                .catch(() => ({ saldo_troca: 0, saldo_programa: 0 }))
            : Promise.resolve({ saldo_troca: 0, saldo_programa: 0 }),
          api.get<{ valor: string }>('/api/app-settings/caixa_destino_default').catch(() => ({ valor: '' })),
          roteiaLivre ? Promise.resolve(null) : api.get<{ id: number; nome: string } | null>('/api/caixa/meu-caixa').catch(() => null),
          api.get<{ valor: string }>('/api/app-settings/programa_prestador_teto_uso_pct').catch(() => ({ valor: '' })),
        ]);
        if (cancelado) return;
        // PROGRAMA DO PRESTADOR (DA-435): pedido com prestador nao oferece Crediario nem Credito da
        // Loja. Crediario porque o credito nasce sobre dinheiro que JA entrou, e o fiado e o unico
        // caso que a trava de pagamento eletronico deixa de fora. Credito da Loja porque ele vira
        // vDesc na nota — gastar credito aqui derrubaria o valor do documento abaixo do que o
        // prestador informou ao cliente dele, que e' a unica coisa que o programa promete.
        //
        // Filtrar UMA vez aqui governa todo dropdown do modal — e e' tirar a ACAO, nao a tela.
        // O backend recusa as duas de novo (nao e cinto+suspensorio: aqui e' UI, la e' garantia).
        setFormas(
          pedido.prestador_id != null
            ? fps.filter((f) => f.tipo !== 'crediario' && f.tipo !== 'credito_loja')
            : fps,
        );
        setMeuCaixa(gavetaPessoal);
        setProgramaBase(det.programa ?? null);
        const saldoTroca = Number(saldoResp.saldo_troca) || 0;
        const saldoPrograma = Number(saldoResp.saldo_programa) || 0;
        setSaldos({ troca: saldoTroca, programa: saldoPrograma });
        // O parse e' a funcao COMPARTILHADA, nao uma copia local: um parse manual aqui ja tinha
        // divergido do servidor no caso do zero (o front lia `0` como "sem limite" e liberava a
        // compra inteira; o backend lia como "suspende o gasto" e recusava). Regra de negocio que
        // vive nos dois lados tem que sair da MESMA funcao.
        setTetoUsoPct(parseTetoUso(cfgTetoUso?.valor));
        const caixaConfigurado = Number(cfgCaixa?.valor) || null;
        setCaixaPreferidoId(caixaConfigurado);

        const pixId = fps.find((f) => f.tipo === 'pix')?.id ?? '';
        const pags = det.pagamentos as Array<{ id: number; forma_pagamento_id: number; valor: string; dias_vencimento: number | null; recebido: string | null; card_parcelas: number | null; conta_cartao: string | null; credito_origem: string | null }>;
        // Helper: caixa default pra uma forma. Prefere o caixa configurado em
        // /configuracoes/recebimentos; fallback pro Banco do Brasil; depois o
        // primeiro caixa que aceita.
        const bancoBrasilId = contas.find((c) => /banco\s*do\s*brasil/i.test(c.nome))?.id;
        // Maquininha escolhida no orcamento -> caixa destino: 'mp' = Mercado Pago (Point),
        // 'pagbank' = PagBank (manual). Preserva a condicao cotada pra vendedora (DA-219).
        const mpCaixaId = contas.find((c) => /mercado\s*pago/i.test(c.nome))?.id;
        const pagbankCaixaId = contas.find((c) => /pag\s*bank|pagbank/i.test(c.nome))?.id;
        const caixaDaContaCartao = (conta: string | null): number | undefined =>
          conta === 'mp' ? mpCaixaId : conta === 'pagbank' ? pagbankCaixaId : undefined;
        const caixaDefault = (fpId: number | ''): number | '' => {
          if (fpId === '') return '';
          const fp = fps.find((f) => f.id === fpId);
          if (!fp || fp.tipo === 'crediario' || fp.tipo === 'credito_loja') return '';
          if (fp.tipo === 'dinheiro' && !roteiaLivre) return gavetaPessoal?.id ?? '';
          // Fechando pedido de OUTRA pessoa: o dinheiro vai pra gaveta DELA. Mesma regra da
          // `caixaDefaultPara` abaixo, que roda quando o operador troca a forma na tela — esta
          // aqui e' a que decide o pedido que ja chega com dinheiro escolhido, que e' o caso comum.
          if (fp.tipo === 'dinheiro') {
            const gavetaVendedor = gavetaDoVendedorDoPedido(fpId);
            if (gavetaVendedor) return gavetaVendedor.id;
            // Sem gaveta pra apontar (inclusive "Ajuda no caixa"): Movimento de Caixa, EXPLICITO.
            // Ate 2026-09-05 ele so aparecia aqui por ser o 1o caixa da lista que aceita dinheiro —
            // default por ordenacao, nao por regra, e foi o que mandou o dinheiro pro lugar errado.
            const mov = contas.find((c) => /movimento/i.test(c.nome) && c.formas_pagamento.some((x) => x.id === fpId));
            if (mov) return mov.id;
          }
          if (caixaConfigurado && contas.some((c) => c.id === caixaConfigurado && c.formas_pagamento.some((x) => x.id === fpId))) {
            return caixaConfigurado;
          }
          if (bancoBrasilId && contas.some((c) => c.id === bancoBrasilId && c.formas_pagamento.some((x) => x.id === fpId))) {
            return bancoBrasilId;
          }
          return contas.find((c) => c.formas_pagamento.some((x) => x.id === fpId))?.id ?? '';
        };

        // Pagamentos guardados so sao reaproveitados se a soma bater com o total do
        // pedido. Quando o orcamento e voltado e editado depois, o total muda mas o
        // pagamento guardado nao — ai pre-preenche uma linha unica com o total
        // (mantendo a forma, se houver so uma guardada) pra nao herdar valor errado.
        const somaPags = pags.reduce((s, p) => s + Number(p.valor || 0), 0);
        const pagsConferem = pags.length > 0 && Math.abs(somaPags - totalOriginalPedido) < 0.01;
        let linhas: PagamentoLinha[];
        if (pagsConferem) {
          linhas = pags.map((p, i) => {
            // Cartao com maquininha escolhida no orcamento herda o caixa destino dela (mp/pagbank);
            // senao cai no default. Isso faz a simulacao no caixa cotar a MESMA adquirente que a
            // vendedora cotou e o fluxo de cobranca certo (MP=Point integrada / PagBank=manual).
            const caixaCartao = caixaDaContaCartao(p.conta_cartao);
            return {
              id: `${p.id}-${i}`,
              forma_pagamento_id: p.forma_pagamento_id,
              valor: Number(p.valor),
              caixa_id: caixaCartao ?? caixaDefault(p.forma_pagamento_id),
              dias_vencimento: p.dias_vencimento ?? undefined,
              recebido: p.recebido != null ? Number(p.recebido) : undefined,
              // Parcela do cartao escolhida no orcamento — o select de parcelas le daqui
              // (nSel = p.card_parcelas ?? 1) e o "cobrar na maquininha" recalcula ao vivo.
              card_parcelas: p.card_parcelas != null ? Number(p.card_parcelas) : undefined,
              conta_cartao: p.conta_cartao ?? undefined,
              // A carteira escolhida la no Enviar ao Caixa viaja ate aqui (DA-436). Ausente vira
              // 'troca' e o bloco abaixo reavalia — pedido antigo nao tem o campo.
              credito_origem: p.credito_origem ? normalizarCarteira(p.credito_origem) : undefined,
            };
          });
        } else {
          const formaInicial = pags.length === 1 ? pags[0]!.forma_pagamento_id : (pedido.forma_pagamento_id ?? pixId);
          linhas = [{ id: 'p0', forma_pagamento_id: formaInicial, valor: totalOriginalPedido, caixa_id: caixaDefault(formaInicial) }];
        }

        // NAO auto-injeta Credito da Loja (decisao Ruan 2026-06-10): a obrigacao de
        // avisar do credito e do cliente. O operador adiciona "Credito da Loja" a mao
        // quando o cliente informa. Antes o sistema preenchia sozinho a partir do
        // saldo, e venda paga 100% no credito passava no automatico sem nota — o que
        // agora tambem e barrado (botao Finalizar trava com valor de nota R$ 0).

        // Qual carteira a venda vai gastar (DA-436). A escolha e' da VENDA, nao da linha — uma
        // compra aceita um unico credito. O que veio do Enviar ao Caixa manda; sem isso, o lado com
        // mais saldo (`carteiraPadrao`, a MESMA regra usada quando o operador cria a linha aqui —
        // duas regras diferentes pro mesmo default fariam a carteira depender de por onde a linha
        // entrou). O operador troca no seletor de qualquer jeito.
        const origemSalva = pags.find((p) => p.credito_origem)?.credito_origem;
        const carteiraInicial: CarteiraCredito = origemSalva
          ? normalizarCarteira(origemSalva)
          : carteiraPadrao(saldoTroca, saldoPrograma);

        // Credito da Loja nunca pode exceder o saldo real do cliente NAQUELA carteira. Orcamento
        // salvo com credito maior que o saldo atual (ex: saldo caiu depois) e capado no saldo; o
        // que sobra do total vira "Restante" pra outra forma.
        const saldoDa = (c: CarteiraCredito) => (c === 'programa' ? saldoPrograma : saldoTroca);
        linhas = linhas
          .map((l) => {
            const fp = fps.find((f) => f.id === l.forma_pagamento_id);
            if (fp?.tipo !== 'credito_loja') return l;
            const teto = saldoDa(carteiraInicial);
            return { ...l, credito_origem: carteiraInicial, valor: l.valor > teto ? +teto.toFixed(2) : l.valor };
          })
          .filter((l) => {
            const fp = fps.find((f) => f.id === l.forma_pagamento_id);
            return !(fp?.tipo === 'credito_loja' && l.valor <= 0.005);
          });

        // Cobranca(s) de cartao JA PAGA(s) deste pedido (recebimentos_vinculados = point_cobrancas
        // pago). Acontece quando a finalizacao barrou DEPOIS de a maquininha aprovar (o dinheiro
        // entrou, a venda ficou orfa) ou o modal foi reaberto. Sem isso a tela ofereceria "Cobrar na
        // maquininha" de novo (cobraria o cliente 2x) e travaria o "Finalizar". Aqui a gente casa
        // cada cobranca paga a uma linha de cartao por valor, marca a linha APROVADA (destrava a
        // nota) e forca o caixa pro Mercado Pago (onde a Point liquida) — SEM montar a peca de
        // cobranca. O backend le a cobranca paga por orcamento_id no finalize e nao recobra.
        // 'pago' = cartão já capturado desta venda; 'a_estornar' = cartão de uma venda CANCELADA sendo
        // REUSADO (DA-280) — o dinheiro já está retido na loja, reaproveita sem recobrar. Os dois casam
        // com a linha de cartão por valor; a diferença é que o 'a_estornar' precisa voltar pra 'pago' no
        // finalize (manda o id em reaproveitar_cobrancas).
        const cartoesPagos = ((det as any).recebimentos_vinculados as any[] ?? [])
          .filter((r) => r.origem === 'cartao' && (r.status === 'pago' || r.status === 'a_estornar'));
        const aprovadoInit: Record<string, boolean> = {};
        const jaPagoInit: Record<string, { valor: number; bandeira: string | null; parcelas: number | null; cobrancaId: number | null; reaproveitar: boolean; ajustadaDe: number | null }> = {};
        if (cartoesPagos.length > 0 && mpCaixaId != null) {
          const usados = new Set<number>();
          linhas = linhas.map((l) => {
            const fp = fps.find((f) => f.id === l.forma_pagamento_id);
            if (fp?.tipo !== 'cartao_credito' && fp?.tipo !== 'cartao_debito') return l;
            // 1) Casamento EXATO (a linha bate o que foi cobrado): caminho de sempre, intocado.
            let idx = cartoesPagos.findIndex(
              (r, i) => !usados.has(i) && Math.abs(Number(r.valor_alocado) - l.valor) < 0.05,
            );
            // 2) Cobertura PARCIAL: a cobranca aprovada vale MENOS que a linha pede. Acontece quando o
            //    pedido volta pro vendedor depois de o cliente pagar e a troca de itens muda o total
            //    (caso VERGIMAR/ORC-006247: cobrado 262,02, pedido virou 263,23). Ate aqui o exato era
            //    tudo-ou-nada: nao batendo, a tela fingia que nada tinha sido pago, oferecia "Cobrar na
            //    maquininha" de novo (cobraria 2x) e travava o Finalizar, com o dinheiro real parado.
            //    Agora a linha CAI pro valor que a maquininha aprovou — verdade medida pelo MP, nao
            //    estimativa — e a diferenca vira "Restante", que o vendedor cobre na forma que quiser.
            //    O maior-que-a-linha (venda encolheu) NAO casa de proposito: sobra de cartao e
            //    devolucao, decisao com porta propria, nunca efeito colateral de um fechamento.
            if (idx < 0) {
              idx = cartoesPagos.findIndex(
                (r, i) => !usados.has(i) && Number(r.valor_alocado) < l.valor - 0.05,
              );
            }
            if (idx < 0) return l;
            usados.add(idx);
            const r = cartoesPagos[idx];
            const pago = Number(r.valor_alocado);
            const parcial = pago < l.valor - 0.05;
            aprovadoInit[l.id] = true;
            jaPagoInit[l.id] = {
              valor: Number(r.valor_recebimento) || l.valor,
              bandeira: r.bandeira ?? null,
              parcelas: r.parcelas != null ? Number(r.parcelas) : null,
              // Reaproveitamento (DA-280): 'a_estornar' = cartão de venda cancelada sendo reusado —
              // manda o id pro finalize voltar a cobrança pra 'pago'. 'pago' já está pago (id não usado).
              cobrancaId: r.cobranca_id != null ? Number(r.cobranca_id) : null,
              reaproveitar: r.status === 'a_estornar',
              ajustadaDe: parcial ? l.valor : null,
            };
            // a cobranca liquidou no Mercado Pago; no parcial a linha vale o que foi pago de verdade
            return { ...l, caixa_id: mpCaixaId, valor: parcial ? pago : l.valor };
          });
        }

        // Pix JA RECEBIDO deste pedido: o backend ja manda no MESMO pacote do cartao (origem 'pix',
        // de mp_vinculos -> mp_recebimentos) — o front so descartava. Casa cada recebimento a uma
        // linha de Pix pelo valor ALOCADO ao pedido (mv.valor), que e o que a linha cobre; um Pix
        // parcial aloca menos que o recebimento inteiro. Marca a linha pra suprimir o "Cobrar Pix" e
        // mostrar o estado real ao operador. (DA-322)
        //
        // ⚠️ [DA-570] Uma linha pode ser coberta por MAIS DE UM Pix, e ate 18/08 isso travava a
        // venda com o dinheiro inteiro no banco. No ORC-006797 o cliente pagou R$ 2,99 (troco de uma
        // compra anterior) + R$ 372,02 numa UNICA linha declarada de R$ 375,01: o casamento era de
        // um recebimento por linha, pegou o PRIMEIRO em ordem cronologica — os R$ 2,99 —, derrubou
        // a linha pra 2,99 e anunciou "FALTA R$ 372,02" num pedido quitado. Agora a linha ACUMULA.
        const pixRecebidos = ((det as any).recebimentos_vinculados as any[] ?? [])
          .filter((r) => r.origem === 'pix' && r.status === 'approved');
        const pixJaPagoInit: Record<string, { valor: number; pagador: string | null }> = {};
        if (pixRecebidos.length > 0) {
          // A decisao (qual Pix cobre qual linha, e quanto) e regra PURA e testada em
          // `@fiodecobre/shared` — aqui fica so a traducao pro formato da tela.
          const casado = casarPixRecebidos(
            linhas
              .filter((l) => fps.find((f) => f.id === l.forma_pagamento_id)?.tipo === 'pix')
              .map((l) => ({ id: l.id, valor: l.valor })),
            pixRecebidos.map((r) => ({ valor: Number(r.valor_alocado), pagador: (r.pagador ?? null) as string | null })),
          );

          // ⚠️ [DA-625] Cobertura PARCIAL parte a linha em DUAS, em vez de so encolhe-la.
          // Encolhendo, o caixa abria o ORC-007160 com uma linha `PIX 2,00` e os R$ 100,01 que
          // faltavam nao tinham onde ser lancados a nao ser por "Adicionar forma de pagamento" —
          // que ninguem adivinha. O gesto natural e mexer na linha que esta na tela, e trocar
          // AQUELA linha pra DINHEIRO soltava o casamento: a venda passava a cobrar R$ 2,00 que ja
          // estavam na conta da loja. Agora a linha que o operador mexe ja nasce com o que FALTA, e
          // a que registra o dinheiro recebido fica travada, fora do caminho.
          linhas = linhas.flatMap((l) => {
            const c = casado[l.id];
            if (!c) return [l];
            const d = dividirLinhaPorPixRecebido(l.valor, c.valor);
            if (d.tipo === 'intacta') return [l];
            if (d.tipo === 'coberta') {
              pixJaPagoInit[l.id] = { valor: c.valor || l.valor, pagador: c.pagador };
              return [l];
            }
            // A fatia RECEBIDA guarda o id original (é ela que espelha a linha declarada) e o valor
            // que de fato caiu na conta — verdade medida no extrato do MP, não declaração. O RESTO
            // nasce como linha nova, livre, na mesma forma/caixa: o operador troca pra dinheiro,
            // cartão ou cobra o Pix que falta.
            pixJaPagoInit[l.id] = { valor: d.recebido, pagador: c.pagador };
            return [
              { ...l, valor: d.recebido },
              { ...l, id: `${l.id}-falta`, valor: d.resto, recebido: undefined },
            ];
          });
        }

        setPagamentos(linhas);
        setCartaoAprovadoLinha(aprovadoInit);
        setCartaoJaPagoLinha(jaPagoInit);
        setCartoesPagosPedido(cartoesPagos);
        setPixJaPagoLinha(pixJaPagoInit);
      } catch (e: any) {
        toast.erro(e.message);
      }
    }
    void carregar();
    return () => { cancelado = true; };
  }, [pedido.id, pedido.cliente_id, totalOriginalPedido, contas, toast]);

  // Provedor Pix ativo: libera o botão "Cobrar Pix" só quando é o gratuito (custo-zero).
  useEffect(() => {
    let cancelado = false;
    api.get<{ configurada: boolean; gratuito: boolean }>('/api/pix/status')
      .then((s) => { if (!cancelado) setPixCobrancaAtiva(!!(s.configurada && s.gratuito)); })
      .catch(() => { /* sem provedor → botão fica oculto */ });
    return () => { cancelado = true; };
  }, []);

  // Maquininha Point configurada (token MP + POINT_DEVICE_ID): libera o botão "Cobrar na maquininha".
  useEffect(() => {
    let cancelado = false;
    api.get<{ configurada: boolean }>('/api/cartao/status')
      .then((s) => { if (!cancelado) setCartaoMaquininha(!!s.configurada); })
      .catch(() => { /* sem maquininha → botão fica oculto, cartão segue manual */ });
    return () => { cancelado = true; };
  }, []);

  // (Pix: cobrar/poll/socket/confirmar/cancelar/comprovante + cancela-no-desmonte agora vivem na peça
  //  única <CobrancaPix>, montada por linha quando pixAtivoLinha[id] liga — ver o JSX da linha abaixo.)

  // (Cartão: cobrar/poll/socket/cancelar + cancela-no-desmonte agora vivem na peça única
  //  <CobrancaCartao> — montada por linha quando cartaoAtivoLinha[id] liga, ver o JSX da linha abaixo.
  //  Aqui o pai só guarda dois flags por linha: ativo (há cobrança rolando → trava o caixa) e aprovado
  //  (a maquininha aprovou → libera a finalização). A peça avisa pelos callbacks onAprovado/onCancelado.)

  // Caixa onde o cartão da maquininha Point liquida: SEMPRE "Mercado Pago" (a maquininha é do MP).
  // Quando a Point é acionada numa linha, o caixa destino dela é forçado pra cá e travado (DA-194) —
  // evita a venda cair no caixa errado (ex: InfinitePay) por esquecimento do operador.
  const mercadoPagoCaixaId = useMemo(
    () => contas.find((c) => /mercado\s*pago/i.test(c.nome))?.id ?? null,
    [contas],
  );

  // Formas LANCADAS que ainda dependem de uma acao no balcao pra virar dinheiro (Pix sem cair,
  // cartao sem aprovar). Alimenta o 4o estado do selo na esquerda. Ver [DA-403].
  // Cartao na maquininha MP e excluido: o badge dele vai pro bloco da maquininha (direita).
  const formasAguardando = pagamentos
    .filter((p) => {
      if (Number(p.valor || 0) <= 0.005 || formaConfirmada(p)) return false;
      const tipo = getFp(p.forma_pagamento_id)?.tipo;
      if ((tipo === 'cartao_credito' || tipo === 'cartao_debito')
          && cartaoMaquininha && mercadoPagoCaixaId != null && p.caixa_id === mercadoPagoCaixaId) return false;
      return true;
    })
    .map((p) => (getFp(p.forma_pagamento_id)?.nome ?? 'PAGAMENTO').toUpperCase());
  const cartaoMaquininhaPendente = cartaoMaquininha && mercadoPagoCaixaId != null && pagamentos.some((p) => {
    const tipo = getFp(p.forma_pagamento_id)?.tipo;
    return Number(p.valor || 0) > 0.005 && !formaConfirmada(p)
      && (tipo === 'cartao_credito' || tipo === 'cartao_debito')
      && p.caixa_id === mercadoPagoCaixaId;
  });

  // Aviso de PARCELADO PELO CLIENTE (DA-516). A maquininha Point PERGUNTA no visor qual é o tipo de
  // parcelamento e aceita "Parcelado vendedor" — e aí a loja banca o juros (~11% do valor). Não há
  // trava do lado do Mercado Pago: a conta já está configurada como parcelado cliente e o aparelho
  // está em modo PDV, e mesmo assim ele pergunta.
  //
  // Modal (e não texto fixo) porque este é o ÚNICO ponto do fluxo em que a venda ainda não aconteceu
  // e o erro dá pra evitar em vez de remediar — depois da aprovação o cartão já foi debitado e a
  // única saída é estorno. Decisão do Ruan, 12/08/2026.
  //
  // Uma vez POR LINHA, não a cada mexida no seletor: comparar 3x com 4x é parte do atendimento, e um
  // modal a cada troca atrapalharia justamente a leitura dos preços que a vendedora precisa fazer.
  const avisadoParceladoRef = useRef<Record<string, boolean>>({});
  function avisarParceladoPeloCliente(lineId: string, parcelas: number) {
    if (parcelas < 2 || avisadoParceladoRef.current[lineId]) return;
    avisadoParceladoRef.current[lineId] = true;
    void dialog.alerta(
      'Na maquininha, selecione "Parcelado pelo Cliente".\n\n'
      + 'Se escolher "Parcelado vendedor", a loja paga os juros do parcelamento '
      + 'e a venda não poderá ser finalizada.',
      { titulo: 'Atenção — parcelamento no cartão', tipo: 'aviso' },
    );
  }

  // Liga a cobrança no cartão da linha: trava o caixa destino no Mercado Pago (a Point é do MP — DA-194)
  // e monta a peça <CobrancaCartao>, que COBRA AO MONTAR e conduz o ciclo (poll/socket/aprovado).
  function iniciarCartao(lineId: string) {
    if (mercadoPagoCaixaId) {
      setPagamentos((arr) => arr.map((x) => (x.id === lineId ? { ...x, caixa_id: mercadoPagoCaixaId } : x)));
    }
    setCartaoAtivoLinha((m) => ({ ...m, [lineId]: true }));
  }

  // Desliga a cobrança da linha (cliente desistiu / trocou forma/valor / removeu a linha): desmonta a
  // peça (que cancela a cobrança no backend no desmonte) e devolve o caixa destino pro default da forma.
  function resetCartaoLinha(lineId: string) {
    setCartaoAtivoLinha((m) => (m[lineId] ? { ...m, [lineId]: false } : m));
    setCartaoAprovadoLinha((m) => (m[lineId] ? { ...m, [lineId]: false } : m));
    setPagamentos((arr) => arr.map((x) => (x.id === lineId ? { ...x, caixa_id: caixaDefaultPara(x.forma_pagamento_id) } : x)));
  }

  function getFp(fpId: number | ''): FormaPagamento | undefined {
    if (fpId === '') return undefined;
    return formas.find((f) => f.id === fpId);
  }

  function caixasParaForma(fpId: number | ''): CaixaConta[] {
    if (fpId === '') return [];
    const fp = formas.find((f) => f.id === fpId);
    // Dinheiro de operadora comum: so a gaveta pessoal dela. Admin/gerente e "Ajuda no caixa"
    // (roteiaLivre) veem a lista geral, com o Movimento de Caixa.
    if (fp?.tipo === 'dinheiro' && !roteiaLivre) {
      return meuCaixa ? contas.filter((c) => c.id === meuCaixa.id) : [];
    }
    return contas.filter((c) => c.formas_pagamento.some((x) => x.id === fpId));
  }

  // A gaveta pessoal do VENDEDOR deste pedido, quando ele nao e quem esta finalizando. Existe
  // porque o dono do sistema fecha pedido de vendedora a noite, de casa: sem isto o dinheiro caia
  // no Movimento de Caixa, que nao fecha diariamente, enquanto a cedula estava na gaveta dela —
  // sobra de caixa sem ninguem para conferir. Retorna undefined quando o vendedor e o proprio
  // operador ou quando ele nao tem gaveta ativa.
  //
  // ⛔ SO admin/gerente (decisao dele, 2026-09-05). "Ajuda no caixa" (JOAO) fica de fora de
  // proposito: o dinheiro que ele fecha continua caindo no Movimento de Caixa, como a [DA-384]
  // desenhou. Nao e' esquecimento — `roteiaLivre` seria o gancho natural aqui e NAO serve.
  function gavetaDoVendedorDoPedido(fpId: number | ''): CaixaConta | undefined {
    if (fpId === '' || !ehAdminGerente) return undefined;
    const vendedorId = pedido.vendedor_id;
    if (vendedorId == null || vendedorId === usuario?.id) return undefined;
    return contas.find((c) => c.usuario_id === vendedorId && c.formas_pagamento.some((x) => x.id === fpId));
  }

  // Caixa default pra uma forma. Prefere o caixa configurado em
  // /configuracoes/recebimentos; fallback pro Banco do Brasil; depois o primeiro
  // caixa valido. Vazio quando forma nao exige caixa (crediario / credito_loja).
  function caixaDefaultPara(fpId: number | ''): number | '' {
    if (fpId === '') return '';
    const fp = formas.find((f) => f.id === fpId);
    if (!fp || fp.tipo === 'crediario' || fp.tipo === 'credito_loja') return '';
    // Dinheiro de operadora comum vai pra gaveta pessoal dela.
    if (fp.tipo === 'dinheiro' && !roteiaLivre) return meuCaixa?.id ?? '';
    // Dinheiro de quem roteia livre (admin/gerente ou "Ajuda no caixa"). Fechando pedido de OUTRA
    // pessoa, o default e a gaveta DELA — quem recebeu a cedula do cliente foi quem vendeu, e o
    // dinheiro fisico esta la. O Movimento de Caixa so entra quando nao ha gaveta pra apontar.
    // ⚠️ O campo segue editavel: isto muda o que vem preenchido, nao o que da pra escolher.
    if (fp.tipo === 'dinheiro') {
      const gavetaDoVendedor = gavetaDoVendedorDoPedido(fpId);
      if (gavetaDoVendedor) return gavetaDoVendedor.id;
      const mov = contas.find((c) => /movimento/i.test(c.nome) && c.formas_pagamento.some((x) => x.id === fpId));
      if (mov) return mov.id;
    }
    if (caixaPreferidoId && contas.some((c) => c.id === caixaPreferidoId && c.formas_pagamento.some((x) => x.id === fpId))) {
      return caixaPreferidoId;
    }
    const bancoBrasilId = contas.find((c) => /banco\s*do\s*brasil/i.test(c.nome))?.id;
    if (bancoBrasilId && contas.some((c) => c.id === bancoBrasilId && c.formas_pagamento.some((x) => x.id === fpId))) {
      return bancoBrasilId;
    }
    return contas.find((c) => c.formas_pagamento.some((x) => x.id === fpId))?.id ?? '';
  }

  // O centavo da calibracao (DA-233) mora na perna do PIX, nunca no dinheiro/cartao (DA-299). Roda
  // sempre que o sistema OU o operador mexe num valor: a perna nova nascia com "o que falta do total"
  // e o centavo caia em quem fosse criada por ultimo — deixava o Pix redondo (perde a marca que o
  // identifica na conciliacao) e a gaveta fechando com centavo. A funcao e conservadora: so mexe no
  // quadro inequivoco (um Pix redondo + uma perna nao-Pix carregando exatamente o centavo do total),
  // e preserva a soma. Fica no estado (nao no submit) pra o operador VER o valor que vai cobrar.
  //
  // ⛔ Pix JA RECEBIDO nao aceita o centavo ([DA-585]): `confirmado` marca a linha cujo Pix ja esta
  // vinculado (ou foi pago nesta tela), e a regra pula essas. Somar 1 centavo a um Pix que ja caiu
  // faz a linha declarar um valor que o banco nao recebeu — e o cerco da nota mede isso com regua
  // de MEIO centavo, entao a venda fecha e a nota fica presa, saindo depois sem painel ([DA-584]).
  function ajustarCentavoPix(arr: PagamentoLinha[]): PagamentoLinha[] {
    const comTipo = arr.map((l) => ({
      linha: l,
      tipo: getFp(l.forma_pagamento_id)?.tipo ?? '',
      valor: Number(l.valor || 0),
      confirmado: !!pixJaPagoLinha[l.id] || !!pixPagoNaSessao[l.id],
    }));
    // Ao mover o centavo de calibracao, empurra o `valorBruto` (anchor) junto do valor pra o desconto
    // derivado (ref − valor) nao oscilar 1 centavo. Linha sem anchor travado segue sem anchor.
    return centavoNaPernaPix(comTipo, totalPedido).map((x) => {
      if (x.valor === x.linha.valor) return x.linha;
      const delta = +(x.valor - x.linha.valor).toFixed(2);
      const valorBruto = x.linha.valorBruto != null ? +(x.linha.valorBruto + delta).toFixed(2) : undefined;
      return { ...x.linha, valor: x.valor, valorBruto };
    });
  }

  function atualizarLinha(idx: number, patch: Partial<PagamentoLinha>) {
    setPagamentos((arr) => ajustarCentavoPix(arr.map((p, i) => {
      if (i !== idx) return p;
      const next = { ...p, ...patch };
      // ⛔ [DA-718] Linha com cobranca JA PAGA na maquininha que segue no cartao fica PRESA no
      // Mercado Pago. O `onChange` da forma manda o caixa default da forma nova, e o Banco do Brasil
      // tambem aceita debito — sem isto, trocar credito->debito numa venda ja paga jogaria a perna
      // pra uma conta onde aquele dinheiro NAO caiu (a Point liquida no MP, DA-194), e o backend
      // aceitaria, porque a conta aceita a forma. A trava e' a mesma da re-hidratacao la em cima.
      if (cartaoJaPagoLinha[p.id] && ehCartao(getFp(next.forma_pagamento_id)?.tipo) && mercadoPagoCaixaId) {
        next.caixa_id = mercadoPagoCaixaId;
      }
      // A linha que ACABOU de virar Credito da Loja precisa nascer com carteira (DA-436). Sem isto
      // ela ficava undefined => lida como 'troca', e o prestador que so tem credito do PROGRAMA
      // caia num beco: a validacao recusava por "saldo R$ 0,00 em Trocas" e nao havia como trocar.
      // E' o caminho NORMAL — desde a decisao de 2026-06-10 o sistema nao auto-injeta credito, o
      // operador adiciona a linha a mao quando o cliente avisa.
      if (getFp(next.forma_pagamento_id)?.tipo === 'credito_loja') {
        if (!next.credito_origem) {
          // HERDA da outra linha de credito, se houver: a carteira e' da VENDA, nao da linha. Sem
          // isto uma 2a linha nasceria pelo saldo maior e podia divergir da 1a — e o backend
          // recusaria com "esta misturando carteiras" numa venda em que ninguem misturou nada.
          const irma = arr.find((l, j) => j !== idx && l.credito_origem
            && getFp(l.forma_pagamento_id)?.tipo === 'credito_loja');
          next.credito_origem = irma?.credito_origem ?? carteiraPadrao(saldos.troca, saldos.programa);
        }
        // A linha que ACABOU de virar credito cai pro que a carteira cobre (pedido dele, 24/10/2026:
        // "ao selecionar Credito da Loja, o sistema ja deduzir do total e mostrar o restante em
        // aberto"). Antes ela ficava com o valor da forma anterior — o total inteiro, mesmo com saldo
        // R$ 0,00 — e a venda caia no "sem valor a cobrar". O que sobra fica sem forma: o selo mostra
        // FALTA R$ X e o "Adicionar forma de pagamento" ja nasce com esse restante. Nao e' a
        // auto-injecao revogada na DA-171: quem escolheu o credito foi o operador.
        if (getFp(p.forma_pagamento_id)?.tipo !== 'credito_loja') {
          const saldo = next.credito_origem === 'programa' ? saldos.programa : saldos.troca;
          const usadoPelasIrmas = arr.reduce((s, l, j) => (j !== idx
            && getFp(l.forma_pagamento_id)?.tipo === 'credito_loja' ? s + Math.round(Number(l.valor || 0) * 100) : s), 0);
          const cabe = Math.max(0, Math.round(saldo * 100) - usadoPelasIrmas);
          next.valor = Math.min(Math.round(Number(next.valor || 0) * 100), cabe) / 100;
        }
      } else if (next.credito_origem) {
        // A linha DEIXOU de ser credito: a carteira tem que ir junto. Enquanto ela ficava
        // pendurada, o `trocarCarteira` nao a alcancava (so percorre linhas de credito) e ela
        // voltava com o valor velho se o operador reescolhesse Credito da Loja depois — dando de
        // novo o "esta misturando carteiras" que a heranca acima existe pra evitar.
        next.credito_origem = undefined;
      }
      return next;
    })));
    // Mudar o valor (ou trocar a forma) invalida o QR já gerado daquela linha — ele cobra o valor
    // antigo. Cancela o QR ativo pra não pagar errado; o operador gera de novo com o novo valor.
    if ('valor' in patch || 'forma_pagamento_id' in patch) {
      const linha = pagamentos[idx];
      // Desliga a cobrança Pix da linha → o <CobrancaPix> desmonta e cancela o QR no backend.
      if (linha) desligarPixLinha(linha.id);
      // Mesma lógica pra a maquininha: mudar valor/forma invalida o que foi mandado pra ela.
      if (linha && cartaoAtivoLinha[linha.id] && !cartaoAprovadoLinha[linha.id]) resetCartaoLinha(linha.id);
      // Linha marcada como JA PAGA (cobrança pré-detectada): mudar o VALOR torna o casamento
      // obsoleto → limpa a pré-aprovação pra não finalizar com um valor que não bate a cobrança.
      //
      // ⛔ [DA-718] Trocar CREDITO <-> DEBITO NÃO invalida nada — é a mesma maquininha, o mesmo
      // pedido e o mesmo valor, e a re-hidratação lá em cima casa as duas formas indistintamente.
      // Limpar aqui fazia a tela voltar a oferecer "Cobrar na maquininha" numa venda JÁ PAGA, que é
      // a cobrança dupla que essa re-hidratação existe pra impedir. Regra pura em `casamento-cartao`.
      const tipoNovoDaLinha = getFp(
        'forma_pagamento_id' in patch ? (patch.forma_pagamento_id ?? '') : (linha?.forma_pagamento_id ?? ''),
      )?.tipo;
      const preAprovacaoSegue = preAprovacaoCartaoSobrevive({
        trocouValor: 'valor' in patch,
        tipoNovo: tipoNovoDaLinha,
      });
      if (linha && cartaoJaPagoLinha[linha.id] && !preAprovacaoSegue) {
        setCartaoAprovadoLinha((m) => { const n = { ...m }; delete n[linha.id]; return n; });
        setCartaoJaPagoLinha((m) => { const n = { ...m }; delete n[linha.id]; return n; });
      }
      // A linha VIROU cartao e o pedido ja tem cobranca paga na maquininha que nenhuma linha assumiu:
      // ela assume agora, igual a abertura faria. Sem isto o pedido salvo em Pix e pago no cartao
      // ficava preso — "Cobrar na maquininha" de novo e Finalizar travado (ORC-008563).
      if (linha && 'forma_pagamento_id' in patch && !cartaoJaPagoLinha[linha.id] && !cartaoAprovadoLinha[linha.id] && mercadoPagoCaixaId) {
        const r = cobrancaPagaParaLinha({
          tipoNovo: tipoNovoDaLinha,
          valorLinha: 'valor' in patch ? Number(patch.valor) : linha.valor,
          cobrancas: cartoesPagosPedido,
          jaUsadas: cartaoPagasConhecidas,
        });
        if (r) {
          setCartaoAprovadoLinha((m) => ({ ...m, [linha.id]: true }));
          setCartaoJaPagoLinha((m) => ({
            ...m,
            [linha.id]: {
              valor: Number(r.valor_recebimento) || linha.valor,
              bandeira: r.bandeira ?? null,
              parcelas: r.parcelas != null ? Number(r.parcelas) : null,
              cobrancaId: r.cobranca_id != null ? Number(r.cobranca_id) : null,
              reaproveitar: r.status === 'a_estornar',
              ajustadaDe: null,
            },
          }));
          // a cobranca liquidou no Mercado Pago (DA-194) — a perna vai pra la, como na abertura
          setPagamentos((arr) => arr.map((p) => (p.id === linha.id ? { ...p, caixa_id: mercadoPagoCaixaId } : p)));
        }
      }
      // Mesma invalidacao pro Pix ja recebido: o casamento era por VALOR, entao mudar valor/forma
      // torna ele obsoleto — solta a marca e o "Cobrar Pix" volta pra o valor novo. (DA-322)
      if (linha && pixJaPagoLinha[linha.id]) {
        setPixJaPagoLinha((m) => { const n = { ...m }; delete n[linha.id]; return n; });
      }
      // O Pix pago nesta sessao valia pro valor ANTIGO da linha — mudou valor/forma, o pagamento
      // deixa de cobrir o que a linha pede e a confirmacao cai junto.
      if (linha && pixPagoNaSessao[linha.id]) {
        setPixPagoNaSessao((m) => { const n = { ...m }; delete n[linha.id]; return n; });
      }
      // [DA-628] O centavo do QR parcial pertencia ao valor ANTIGO da linha. O operador acabou de
      // reescrever a linha, entao o QR sera cancelado no desmonte — e o desmonte NAO chama
      // `onCancelado` (de proposito, pra nao atualizar componente ja desmontado): sem esta chamada o
      // centavo ficaria orfao no total por esta porta. Nao restaura o valor — ele digitou outro.
      if (linha) desfazerAjusteQr(linha.id, false);
    }
  }

  function adicionarLinha() {
    const pixId = formas.find((f) => f.tipo === 'pix')?.id ?? '';
    const valorRestante = +(totalPedido - somaPagamentos).toFixed(2);
    // Título já quitado (nada a pagar): não cria linha zerada — ela só viraria um
    // R$ 0,00 que trava a finalização ("Valor deve ser maior que zero").
    if (valorRestante <= 0.005) {
      toast.erro('Título já quitado — não há valor restante pra adicionar.');
      return;
    }
    setPagamentos((arr) => ajustarCentavoPix([...arr, {
      id: `p${Date.now()}`,
      forma_pagamento_id: pixId,
      valor: valorRestante > 0 ? valorRestante : 0,
      caixa_id: caixaDefaultPara(pixId),
    }]));
  }

  function removerLinha(idx: number) {
    const linha = pagamentos[idx];
    // Pix: remover a linha desmonta o <CobrancaPix>, que cancela o QR pendente no backend sozinho.
    // Linha de cartão com cobrança não aprovada na maquininha: cancela antes de remover.
    if (linha && cartaoAtivoLinha[linha.id] && !cartaoAprovadoLinha[linha.id]) resetCartaoLinha(linha.id);
    // [DA-628] A linha sai de cena levando o centavo do QR parcial dela — o total volta ao que era.
    if (linha) desfazerAjusteQr(linha.id, false);
    setPagamentos((arr) => arr.filter((_, i) => i !== idx));
  }

  // Reencaixa os pagamentos no novo total apos dar/tirar desconto: preserva as linhas de
  // Credito da Loja (capadas no que ja estavam) e coloca o restante numa unica linha na
  // forma que ja estava em uso (ou Pix). O operador rearruma o split depois, se quiser.
  function refitPagamentos(novoTotal: number) {
    setPagamentos((prev) => {
      // ⛔ [DA-626] Linha FIXA nao entra no refit — ele COLAPSA as demais numa so, e colapsar por
      // cima de uma linha de Pix ja confirmado a faria afirmar um valor que o banco nunca recebeu
      // (medido em prod: com o Pix parcial da [DA-625] a tela virou `PIX 102,00 · PIX RECEBIDO`
      // com R$ 2,00 caidos). Credito da Loja ja era fixo pelo mesmo motivo: o saldo e' um fato,
      // nao uma fatia ajustavel. O refit so redistribui o que ainda esta em aberto.
      const ehFixa = (l: PagamentoLinha) => {
        const t = getFp(l.forma_pagamento_id)?.tipo;
        if (t === 'credito_loja') return true;
        return t === 'pix' && (!!pixJaPagoLinha[l.id] || !!pixPagoNaSessao[l.id]);
      };
      const fixas = prev.filter(ehFixa);
      const fixaSoma = fixas.reduce((s, l) => s + Number(l.valor || 0), 0);
      const restante = Math.max(0, +(novoTotal - fixaSoma).toFixed(2));
      const livre = prev.find((l) => !ehFixa(l));
      // O fixo ja cobre o total: nao inventa uma linha de R$ 0,00 (ela so travaria a finalizacao
      // com "Valor deve ser maior que zero"). So acontece com Pix/credito cobrindo tudo.
      if (restante <= 0.005 && fixas.length > 0) return fixas;
      const pixId = formas.find((f) => f.tipo === 'pix')?.id ?? '';
      const formaBase = livre?.forma_pagamento_id ?? pixId;
      const linhaBase: PagamentoLinha = {
        id: livre?.id ?? 'p0',
        forma_pagamento_id: formaBase,
        valor: restante,
        // Preserva o caixa destino ja escolhido na linha (ex.: Mercado Pago pra maquininha Point);
        // so cai no default quando a linha nao tinha caixa. Antes resetava sempre e derrubava a
        // auto-selecao da maquininha ao remover o desconto por causa do cartao (DA-076).
        caixa_id: livre?.caixa_id ?? caixaDefaultPara(formaBase),
        dias_vencimento: livre?.dias_vencimento,
        recebido: undefined,
      };
      return [...fixas, linhaBase];
    });
  }

  // Aplica um total alvo exato (vindo do campo % ou do campo Total). Acima de 5% pede senha
  // gerencial (admin/gerente passam direto). Atualiza o % exibido derivado do total.
  function aplicarTotalAlvo(novoTotal: number) {
    const t = Math.max(0, Math.round(novoTotal * 100) / 100);
    // Teto de acrescimo (gemeo do overpagamento): editar o total pra cima nao pode passar de 10%
    // acima do preco cheio. Barra ANTES de aplicar, sem excecao de perfil (teto duro), com aviso
    // limpo. Desconto (t < cheio) nunca dispara.
    if (acrescimoAcimaDoTeto(cheio, t)) {
      dialog.alerta(
        `O total (${formatMoeda(t)}) passa de ${TETO_ACRESCIMO_PCT}% acima do preço de tabela `
        + `(${formatMoeda(cheio)}). Confira o valor — um acréscimo tão alto costuma ser erro de digitação.`,
        { titulo: 'Acréscimo acima do limite', tipo: 'aviso' },
      );
      return;
    }
    const pctEff = cheio > 0 ? (1 - t / cheio) * 100 : 0;
    if (pctEff > 5.1 && !ehAdminGerente && !senhaGerencial) {
      setPendenteAplicar(t);
      setShowSenhaDesc(true);
      return;
    }
    setTotalAlvo(t);
    setDescontoInput(pctEff > 0.005 ? fmtPct(Math.round(pctEff * 100) / 100) : '');
    refitPagamentos(t);
  }

  // Aplicar pelo campo de %: arredonda o total em 3 faixas (mesma regra do orcamento).
  function aplicarDescontoCaixa() {
    const val = Math.max(0, parseFloat((descontoInput || '0').replace(',', '.')) || 0);
    aplicarTotalAlvo(totalComDescontoPct(cheio, val));
  }

  // Editar direto pelo campo Total: respeita os centavos digitados (sem arredondar pra cima).
  function editarTotalCaixa(novoTotal: number) {
    if (Math.abs(novoTotal - totalPedido) < 0.005) return; // sem mudanca real
    aplicarTotalAlvo(novoTotal);
  }

  function removerDescontoCaixa() {
    const t = Math.round(cheio * 100) / 100; // volta ao preco cheio (sem desconto)
    setTotalAlvo(t);
    setDescontoInput('');
    refitPagamentos(t);
  }

  // Referencia (valor CHEIO) de uma linha de pix/dinheiro: a alocacao original antes de qualquer
  // ajuste. Trava na 1a mexida (`valorBruto`); enquanto ninguem mexeu, e o proprio valor atual.
  function refLinha(linha: PagamentoLinha): number {
    return linha.valorBruto ?? +(Number(linha.valor || 0) + Number(linha.desconto || 0)).toFixed(2);
  }

  // Cobertura PARCIAL do cartao (o pedido mudou depois de o cliente pagar): a tarja que explica a
  // diferenca tem que ler o estado AO VIVO. Ate a DA-403 ela era montada com o valor congelado na
  // abertura do modal, entao seguia mandando "cobre os R$ 1,21 que faltam" DEPOIS de o operador ter
  // coberto/perdoado a diferenca — enquanto o selo do ledger, do outro lado da tela, ja dizia
  // QUITADO. Duas afirmacoes opostas na mesma tela, e a que fica ao lado da mao dele era a errada.
  //
  // `gap` e fato historico do cartao (o que a cobranca deixou de pagar) e por isso segue congelado;
  // o que passa a ser vivo e COMO ele foi coberto: as demais formas + o desconto dado no fechamento.
  // A linha EXISTIR nao quer dizer que a loja recebeu — foi assim que a 1a versao desta tarja
  // cantou "resolvida: R$ 1,21 em PIX" com o "Cobrar Pix" ainda intocado ao lado. Uma forma so
  // fecha a diferenca quando nao falta mais NENHUMA acao no balcao:
  //   dinheiro/credito da loja -> o valor ja esta na mao (o operador digita ao receber);
  //   crediario                -> nao ha o que fazer no balcao (vira titulo; o selo A RECEBER conta);
  //   pix                      -> so com o Pix CAIDO (vinculado na abertura ou pago agora, onPago);
  //   cartao                   -> so aprovado na maquininha.
  // Pix/cartao lancados e nao confirmados vao pra `pendentes` e a tarja NAO declara quitado.
  function formaConfirmada(p: PagamentoLinha): boolean {
    const t = getFp(p.forma_pagamento_id)?.tipo;
    if (t === 'pix') return !!pixJaPagoLinha[p.id] || !!pixPagoNaSessao[p.id];
    if (t === 'cartao_credito' || t === 'cartao_debito') return !!cartaoAprovadoLinha[p.id];
    return true; // dinheiro, credito_loja, crediario
  }

  function resolucaoDaDiferenca(linhaCartao: PagamentoLinha, ajustadaDe: number) {
    const gap = Math.max(0, +(ajustadaDe - Number(linhaCartao.valor || 0)).toFixed(2));
    const outras = pagamentos
      .filter((p) => {
        const t = getFp(p.forma_pagamento_id)?.tipo;
        return p.id !== linhaCartao.id && t !== 'cartao_credito' && t !== 'cartao_debito' && Number(p.valor || 0) > 0.005;
      })
      // `nome` vem capitalizado do banco ("Dinheiro"); a tela e caixa alta por CSS, entao sem o
      // toUpperCase a frase sai "R$ 1,00 em Dinheiro" no meio de tudo em maiuscula.
      .map((p) => ({
        nome: (getFp(p.forma_pagamento_id)?.nome ?? 'OUTRA').toUpperCase(),
        valor: Number(p.valor),
        confirmada: formaConfirmada(p),
      }));
    const somaOutras = +outras.reduce((s, o) => s + o.valor, 0).toFixed(2);
    // O que sobrou do gap depois das outras formas so pode ter saido de desconto (a venda fechou).
    const desconto = +Math.max(0, gap - somaOutras).toFixed(2);
    const partes = outras.filter((o) => o.confirmada).map((o) => `${formatMoeda(o.valor)} em ${o.nome}`);
    if (desconto > 0.005) partes.push(`${formatMoeda(desconto)} de desconto`);
    return {
      gap,
      partes,
      // Coberto de VERDADE = a conta fecha E nao sobrou forma esperando confirmacao.
      coberto: diferenca <= 0.005 && outras.every((o) => o.confirmada),
    };
  }

  // Aplica um desconto (em R$) numa linha de pix/dinheiro. O `valor` vira o LIQUIDO (ref − desconto)
  // e o total da venda cai junto — o "Restante" continua zerado (e desconto, nao falta). Cartao nunca
  // entra aqui (paga cheio, DA-076).
  //
  // Trava de 5% POR LINHA (decisao Ruan 2026-07-15): mede o % sobre a REFERENCIA da linha (desconto ÷
  // ref), nao o % da venda — quem digita "10%" espera que 10% trave, ainda que a linha seja pequena
  // perto do total. Admin/gerente nunca sao solicitados (sao a autorizacao). A blindagem de servidor
  // segue medindo o desconto sobre a VENDA (o backend so recebe o total) — o que ja capa a margem real.
  function aplicarDescLinha(idx: number, descRaw: number) {
    const linha = pagamentos[idx];
    if (!linha) return;
    const ref = refLinha(linha);
    const novo = editarDescontoDaLinha(ref, descRaw);
    const pctLinha = pctDescontoDaLinha(novo.bruto, novo.desconto);
    if (pctLinha > 5.1 && !ehAdminGerente && !senhaGerencial) {
      setPendenteDescLinha({ idx, desconto: novo.desconto });
      setShowSenhaDesc(true);
      return;
    }
    setPagamentos((arr) => arr.map((p, i) => (i === idx ? { ...p, valor: novo.valor, desconto: novo.desconto, valorBruto: novo.bruto } : p)));
    // O valor cobrado mudou → invalida um QR/cobranca Pix ja gerado nessa linha (cobrava o valor antigo).
    desligarPixLinha(linha.id);
  }

  // Edita DIRETO o valor cobrado de UMA linha, seja qual for a forma: ALOCACAO pura (DA-582).
  // Baixar deixa o resto FALTANDO — o painel troca `TOTAL` por `FALTA`, o "Adicionar forma de
  // pagamento" acende e os botoes de nota travam ate a soma cobrir. Nao mexe no total da venda.
  //
  // ⛔ Ate 19/08/2026 descer o valor de pix/dinheiro virava DESCONTO (regra de 15/07). Isso fechava
  // a unica porta de pagamento dividido: numa venda de 420,01, cobrar 175,00 no Pix derrubava a
  // venda inteira pra 175,00, e o botao de adicionar forma — que so acende quando falta valor —
  // ficava cinza orientando "baixe o valor de uma linha", o ato que fechava a porta. O desconto tem
  // campo proprio (`Desc. R$` / `%`), que segue com o portao de senha gerencial acima de 5%.
  //
  // O teto de digitacao (DA-403) FICA: mede 10% acima do preco CHEIO DA VENDA (`acrescimoAcimaDoTeto`,
  // a mesma regra do campo Total e do backend), e e o que barra erro de digitacao — digitar 500 numa
  // venda de 263. A base e a venda, nao a linha: uma linha de R$ 1,00 precisa poder absorver os
  // R$ 1,21 que de fato faltam sem ouvir "aumento maximo R$ 1,10".
  function editarValorLinha(idx: number, v: number) {
    const linha = pagamentos[idx];
    if (!linha) return;
    const nv = Math.max(0, Math.round(v * 100) / 100);
    if (acrescimoAcimaDoTeto(cheio, nv)) {
      void dialog.alerta(
        `O valor (${formatMoeda(nv)}) passa de ${TETO_ACRESCIMO_PCT}% acima do preço de tabela da venda `
        + `(${formatMoeda(cheio)}). Confira — um valor tão alto numa linha costuma ser erro de digitação.`,
        { titulo: 'Valor acima do limite', tipo: 'aviso' },
      );
      return;
    }
    const novo = realocarValorDaLinha(Number(linha.desconto || 0), nv);
    // Via `atualizarLinha` pra herdar as invalidacoes de quem depende do valor da linha (QR do Pix,
    // maquininha, casamento de cobranca ja paga) e o `ajustarCentavoPix` — uma porta so.
    atualizarLinha(idx, { valor: novo.valor, valorBruto: novo.bruto, desconto: novo.desconto });
  }

  // Cartao paga cheio na SUA fatia (DA-076 / refino DA-274): se o pedido veio com desconto de pix
  // e entra uma forma de cartao, cada real cobrado no cartao passa a pagar o preco CHEIO (sem o
  // desconto), e o resto em pix/dinheiro MANTEM o desconto. Aqui so fazemos o rebase das linhas de
  // cartao (do valor-pix pro valor-cheio) uma vez, no instante da troca — a divisao proporcional em
  // si vem do `totalPedido` (regra compartilhada) e do backend (passo 4). Fecha o furo de receber o
  // valor de pix no cartao: a maquininha cobra o cheio e a soma dos pagamentos bate com o total (a
  // nota nunca sai furada). Ao tirar o cartao, devolve o desconto do pedido. Reage so a troca de forma.
  useEffect(() => {
    const temDescontoPix = emCentavos(cheio) > emCentavos(basePix);
    if (temCartao && temDescontoPix && !descontoRemovidoPorCartao) {
      // Rebase: linha que carregava o pix passa a cobrar o cheio (fatia parcial converte proporcional).
      const fator = basePix > 0 ? cheio / basePix : 1;
      setPagamentos((prev) => prev.map((l) => {
        const t = getFp(l.forma_pagamento_id)?.tipo;
        return (t === 'cartao_credito' || t === 'cartao_debito')
          ? { ...l, valor: Math.round(Number(l.valor || 0) * fator * 100) / 100 }
          : l;
      }));
      setDescontoRemovidoPorCartao(true);
      // Avisa UMA vez, com o total MISTO real (cartao cheio + resto pix com desconto).
      const para = totalComCartaoParcial(cheio, basePix, valorCartaoLinhas * fator);
      void dialog.alerta(
        `A parte no cartao passa a cobrar o preco cheio (sem o desconto de pix). O restante em pix ou dinheiro mantem o desconto.\n\nDe ${formatMoeda(basePix)} para ${formatMoeda(para)}.`,
        { titulo: 'Cartao paga o preco cheio', tipo: 'aviso' },
      );
    } else if (!temCartao && descontoRemovidoPorCartao) {
      setTotalAlvo(null);
      setDescontoInput(pctInicial > 0 ? fmtPct(pctInicial) : '');
      refitPagamentos(totalOriginalPedido);
      setDescontoRemovidoPorCartao(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temCartao]);

  // Venda sem valor a cobrar: o Credito da Loja cobre o total (vira vDesc na nota,
  // DA-049), entao a nota sairia por R$ 0 e nao e emitida. Decisao Ruan 2026-06-10:
  // nao existe venda sem valor — barra o botao. Tambem barrado no backend.
  // ⛔ Sem excecao (Ruan 2026-10-03): existia o botao "Finalizar Venda 100% Credito da Loja", que
  // finalizava como Troca sem nota e aparecia por cima da marcacao da vendedora. "Troca ou qualquer
  // outro tipo, se o valor pago e zero nao ha como ser finalizada." Achado no ORC-008544.
  const semValorACobrar = valorNota <= 0.005;

  // TRAVA ANTI-ERRO do cartão na maquininha: quando a maquininha Point está configurada, toda linha
  // de cartão de crédito SÓ libera a finalização depois que o intent APROVA batendo o valor (o
  // sistema manda o valor exato; o operador não digita). Sem a maquininha configurada, o fluxo
  // antigo (bandeira + autorização manuais) segue valendo e não bloqueia.
  const cartaoBloqueiaFinalizar = useMemo(() => {
    if (!cartaoMaquininha) return false;
    return pagamentos.some((p) => {
      const fp = formas.find((f) => f.id === p.forma_pagamento_id);
      if ((fp?.tipo !== 'cartao_credito' && fp?.tipo !== 'cartao_debito') || Number(p.valor) <= 0.005) return false;
      // A maquininha Point é exclusiva do caixa Mercado Pago. Se o cartão vai pra outro
      // caixa (InfinitePay, PagBank...) o fluxo manual (bandeira + autorização) é válido.
      if (mercadoPagoCaixaId && p.caixa_id !== mercadoPagoCaixaId) return false;
      return !cartaoAprovadoLinha[p.id];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartaoMaquininha, pagamentos, formas, cartaoAprovadoLinha, mercadoPagoCaixaId]);

  // ESPELHO da trava do backend pro PIX (DA-583). O servidor ja recusava finalizar com Pix
  // declarado e nao confirmado (`reunirParcelasEletronicas` + a checagem `pagamento_nao_confirmado`
  // em `caixa.routes.ts`, valvula DA-248) — mas o FRONT nao sabia disso e mantinha os botoes de
  // nota acesos. O operador so descobria depois do clique, com o cliente na frente, por uma
  // mensagem de erro; o cartao ja tinha o gemeo (`cartaoBloqueiaFinalizar`), o Pix nunca teve.
  //
  // Conta igual a do backend — soma DECLARADA contra soma CONFIRMADA, com a mesma tolerancia de
  // 5 centavos (variacao do QR estatico, em que o cliente digita o valor) — e nao linha a linha:
  // um pedido pode ter uma linha coberta por dois Pix ([DA-570]), e contar por linha divergiria do
  // servidor justo no caso torto. Confirmado = vinculado na abertura (`pixJaPagoLinha`) ou pago
  // agora nesta tela (`pixPagoNaSessao`).
  //
  // ⛔ Valvula IDENTICA a do servidor: `pix_validado_admin` (o "Validar" da fila) zera a trava —
  // e o caminho pro Pix que caiu numa conta que a loja nao captura (BB/Cora/PagBank, que a
  // integracao do Mercado Pago nunca vera) e pra decisao gerencial. Sem ela, esses casos ficariam
  // sem saida NENHUMA na tela.
  const pixBloqueiaFinalizar = useMemo(() => {
    if (pedido.pix_validado_admin) return false;
    const linhasPix = pagamentos.filter(
      (p) => formas.find((f) => f.id === p.forma_pagamento_id)?.tipo === 'pix' && Number(p.valor || 0) > 0.005,
    );
    const declarado = linhasPix.reduce((s, p) => s + Number(p.valor || 0), 0);
    if (declarado <= 0.005) return false;
    const confirmado = linhasPix.reduce((s, p) => {
      if (pixJaPagoLinha[p.id]) return s + Number(pixJaPagoLinha[p.id]!.valor || 0);
      if (pixPagoNaSessao[p.id]) return s + Number(p.valor || 0);
      return s;
    }, 0);
    return Math.round((declarado - confirmado) * 100) / 100 > TOLERANCIA_CENTAVO_CALIBRACAO;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagamentos, formas, pixJaPagoLinha, pixPagoNaSessao, pedido.pix_validado_admin]);

  // TRAVA DE PERMISSAO: finalizar venda no Crediario (qualquer linha em fiado) e exclusivo
  // de admin/gerente. Operador comum nem com a venda montada consegue concluir — sem atalho
  // de senha (decisao Ruan 2026-06-24). Blindado tambem no backend (validacao no front E no back).
  const crediarioBloqueado = !ehAdminGerente && pagamentos.some(
    (p) => formas.find((f) => f.id === p.forma_pagamento_id)?.tipo === 'crediario' && Number(p.valor) > 0.005,
  );

  // PROGRAMA DO PRESTADOR (DA-436) — a carteira que esta venda gasta e o teto de uso.
  //
  // A escolha e' da VENDA, nao da linha: uma compra aceita um unico credito, entao todas as linhas
  // de Credito da Loja andam juntas. Le da 1a linha de credito; sem linha nenhuma, 'troca'.
  const carteiraDaVenda: CarteiraCredito = useMemo(() => {
    const l = pagamentos.find((p) => getFp(p.forma_pagamento_id)?.tipo === 'credito_loja');
    return l?.credito_origem ?? 'troca';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagamentos, formas]);

  const saldoDaCarteira = carteiraDaVenda === 'programa' ? saldos.programa : saldos.troca;

  // Teto de uso: so morde o credito do PROGRAMA. O de troca e' dinheiro do proprio cliente voltando.
  // Base = total da venda ANTES do abate, senao o teto se morderia.
  const tetoCreditoPrograma = carteiraDaVenda === 'programa' ? tetoUsoPrograma(totalPedido, tetoUsoPct) : null;
  const creditoAcimaDoTeto = tetoCreditoPrograma != null
    && Math.round(creditoLojaTotal * 100) > Math.round(tetoCreditoPrograma * 100);

  // Troca a carteira da venda inteira e RECAPA o valor no saldo do novo lado — trocar pra uma
  // carteira com menos saldo e deixar o numero antigo na tela produziria uma recusa no Finalizar
  // que a tela ja sabia que ia acontecer (principio 1: nao esconder o estado do operador).
  function trocarCarteira(nova: CarteiraCredito) {
    // O saldo e' da CARTEIRA, nao de cada linha: com duas linhas de credito o capamento tem que
    // consumir um restante compartilhado, senao duas linhas de R$ 300 sobreviveriam a um saldo de
    // R$ 500 (cada uma cabe sozinha) e a venda so morreria la no `consumirCredito`.
    let restante = Math.round((nova === 'programa' ? saldos.programa : saldos.troca) * 100);
    setPagamentos((prev) => prev.map((l) => {
      if (getFp(l.forma_pagamento_id)?.tipo !== 'credito_loja') return l;
      const cabe = Math.min(Math.round(l.valor * 100), Math.max(0, restante));
      restante -= cabe;
      return { ...l, credito_origem: nova, valor: cabe / 100 };
    }));
  }

  // `classePag === 'abaixo'`: as formas lancadas nao cobrem o total da venda. Ate aqui o botao da
  // nota ficava ACESO e a recusa so aparecia DEPOIS do clique, num alerta — ou seja, a tela
  // oferecia emitir documento fiscal de uma venda que ela mesma sabia que nao fecha. Decisao do
  // Ruan (2026-08-13): titulo nao quitado, botao de nota indisponivel. O motivo ja esta escrito e
  // grande no selo de estado ao lado ("FALTA R$ X"), que e o que o principio "botao desabilitado
  // DIZ por que" exige — o title do botao repete pra quem passa o mouse. Crediario NAO cai aqui:
  // a linha de fiado cobre o total (o selo diz "A RECEBER"), e a nota tem que sair.
  const finalizarBloqueado = semValorACobrar || cartaoBloqueiaFinalizar || pixBloqueiaFinalizar
    || crediarioBloqueado || creditoAcimaDoTeto || classePag === 'abaixo';

  async function finalizar(tipoDoc: 'nfce' | 'nfe' | 'troca', forcarPagamento = false) {
    if (crediarioBloqueado) {
      toast.erro('Operação permitida apenas para Gerentes');
      return;
    }
    if (cartaoBloqueiaFinalizar) {
      toast.erro('Cobre o cartão na maquininha primeiro: o valor só é confirmado quando a maquininha APROVAR. Toque "Cobrar na maquininha" e peça o cartão ao cliente.');
      return;
    }
    if (pixBloqueiaFinalizar) {
      toast.erro('O Pix desta venda ainda não caiu. Toque "Cobrar Pix" e espere o cliente pagar — se ele já pagou por fora, amarre o recebimento na Conciliação Bancária. Um admin pode usar o "Validar" do pedido.');
      return;
    }
    if (semValorACobrar) {
      toast.erro('Venda sem valor a cobrar: o crédito da loja cobre o total e zeraria a nota. Adicione a forma que o cliente vai pagar (Pix, dinheiro, cartão) — não dá pra finalizar uma venda 100% no crédito.');
      return;
    }
    // Pagamento vs total (regra unica front/back). Abaixo do total ou acima de 10% barra com um
    // aviso limpo (modal padrao, sem jargao); acima do total ate 10% e acrescimo aceito e segue.
    if (classePag === 'abaixo') {
      await dialog.alerta(
        `O valor recebido (${formatMoeda(somaPagamentos)}) está abaixo do total da venda (${formatMoeda(totalPedido)}). `
        + `Falta ${formatMoeda(diferenca)}. Ajuste os pagamentos antes de finalizar.`,
        { titulo: 'Falta valor pra fechar a venda', tipo: 'aviso' },
      );
      return;
    }
    if (classePag === 'acima_teto') {
      await dialog.alerta(
        `O valor recebido (${formatMoeda(somaPagamentos)}) passa de ${TETO_ACRESCIMO_PCT}% acima do total da venda `
        + `(${formatMoeda(totalPedido)}). Confira os valores — um acréscimo tão alto costuma ser erro de digitação.`,
        { titulo: 'Acréscimo acima do limite', tipo: 'aviso' },
      );
      return;
    }
    if (tipoDoc === 'nfe' && nfeIndisponivel) {
      toast.erro('NF-e exige cliente cadastrado');
      return;
    }
    // Linhas zeradas (ex.: "+ Adicionar forma" clicado com a venda já quitada, ou valor
    // apagado à mão) NÃO travam mais a finalização — simplesmente não entram na venda.
    const pagsEfetivos = pagamentos.filter((p) => Number(p.valor) > 0.005);
    if (pagsEfetivos.length === 0) {
      toast.erro('Adicione ao menos uma forma de pagamento com valor.');
      return;
    }
    // O credito lancado nao pode passar do saldo DAQUELA carteira (DA-436). Confere a SOMA das
    // linhas de credito, nao cada uma: com duas linhas de R$ 300 contra R$ 500 de saldo, cada uma
    // passa sozinha e so o `consumirCredito` recusaria — um erro seco depois do Finalizar, que e'
    // justamente o que esta checagem existe pra evitar. O backend confere igual (soma a carteira).
    const totalCreditoEfetivo = pagsEfetivos.reduce(
      (s, p) => (getFp(p.forma_pagamento_id)?.tipo === 'credito_loja' ? s + Number(p.valor) : s), 0,
    );
    if (Math.round(totalCreditoEfetivo * 100) > Math.round(saldoDaCarteira * 100)) {
      const nome = carteiraDaVenda === 'programa' ? 'de Parceiro FIODECOBRE' : 'de Trocas e Devoluções';
      toast.erro(`O crédito ${nome} tem só ${formatMoeda(saldoDaCarteira)} de saldo, e foram lançados ${formatMoeda(totalCreditoEfetivo)}.`);
      return;
    }
    for (const p of pagsEfetivos) {
      const fp = getFp(p.forma_pagamento_id);
      if (!fp) { toast.erro('Selecione a forma de pagamento'); return; }
      const isCrediario = fp.tipo === 'crediario';
      const isCreditoLoja = fp.tipo === 'credito_loja';
      if (!isCrediario && !isCreditoLoja && !p.caixa_id) {
        toast.erro(`Selecione o caixa destino pra ${fp.nome}`); return;
      }
      // Cartão cobrado e APROVADO na maquininha Point dispensa a digitação manual (o retorno traz
      // bandeira/NSU/autorização). Sem a maquininha, segue exigindo bandeira + cód. de autorização.
      const aprovadoNaMaquininha = (fp.tipo === 'cartao_credito' || fp.tipo === 'cartao_debito') && !!cartaoAprovadoLinha[p.id];
      if ((fp.tipo === 'cartao_credito' || fp.tipo === 'cartao_debito')
          && !aprovadoNaMaquininha
          && (!p.card_bandeira || !p.card_cod_autoriz)) {
        toast.erro('Bandeira e codigo de autorizacao obrigatorios pra cartao'); return;
      }
    }

    setEnviando(tipoDoc);
    try {
      // Reaproveitamento (DA-280): cobranças 'a_estornar' (cartão de venda cancelada) que foram casadas
      // a uma linha de cartão APROVADA e vão de fato lastrear esta venda. O backend volta essas — e só
      // essas — pra 'pago'. Linha de cartão sem reuso (cobrança nova ao vivo) não entra aqui.
      const reaproveitarIds = pagsEfetivos
        .filter((p) => cartaoAprovadoLinha[p.id] && cartaoJaPagoLinha[p.id]?.reaproveitar)
        .map((p) => cartaoJaPagoLinha[p.id]!.cobrancaId)
        .filter((cid): cid is number => cid != null);
      // Desconto do fechamento = campo % do topo (basePix) + Desc R$/% nas linhas de pix/dinheiro.
      // O total desejado ja e o `totalPedido` (liquido de ambos). Enviamos o basePix EQUIVALENTE:
      // sem cartao e o proprio total; com cartao inverte o re-inflar (totalComCartaoParcial).
      const querDesconto = descontoAlterado || descontoLinhas > 0.005;
      const basePixEnviar = temCartao ? basePixParaTotal(cheio, totalPedido, valorCartaoLinhas) : totalPedido;
      const descontoTotalPct = cheio > 0 ? Math.max(0, Math.round((1 - totalPedido / cheio) * 10000) / 100) : 0;
      const payload = {
        pagamentos: pagsEfetivos.map((p) => {
          // Acrescimo (juros+imposto) da parcela escolhida = total cobrado - tabela (cheio).
          // So pra cartao de credito com parcela selecionada; vira vOutro na nota (DA-140).
          let card_acrescimo: number | undefined;
          const fp = getFp(p.forma_pagamento_id);
          if (fp?.tipo === 'cartao_credito') {
            const est = parcelamentos[p.id];
            const sel = est?.data?.parcelas.find((x) => x.n === (p.card_parcelas ?? 1));
            if (sel && est?.data) {
              const a = (sel.total - est.data.tabela) / 100;
              if (a > 0) card_acrescimo = Math.round(a * 100) / 100;
            }
          }
          return {
            forma_pagamento_id: p.forma_pagamento_id,
            valor: Number(p.valor),
            caixa_id: p.caixa_id || null,
            dias_vencimento: p.dias_vencimento,
            card_bandeira: p.card_bandeira,
            card_cod_autoriz: p.card_cod_autoriz,
            card_nsu: p.card_nsu,
            card_parcelas: p.card_parcelas,
            card_acrescimo,
            // Qual carteira gastar (DA-436). So faz sentido na linha de Credito da Loja; nas
            // outras vai undefined pra nao inventar um dado que ninguem escolheu.
            credito_origem: fp?.tipo === 'credito_loja' ? (p.credito_origem ?? 'troca') : undefined,
          };
        }),
        cpf_nota: cpfNota.replace(/\D/g, '') || null,
        tipo_documento: tipoDoc,
        // So envia desconto quando o operador mexeu (campo % do topo OU Desc R$/% nas linhas de
        // pix/dinheiro) — caso contrario o fluxo segue identico. O total desejado e o `totalPedido`
        // (liquido de AMBOS os descontos). Manda o basePix EQUIVALENTE (inverso do re-inflar do cartao):
        // o backend reprecifica pro pix (passo 3.5) e re-infla a fatia do cartao (passo 4) batendo esse
        // total. Sem cartao o basePix e o proprio total. Nao mandar o total misto aqui — o passo 4
        // contaria o cartao duas vezes.
        desconto_pct: querDesconto ? descontoTotalPct : undefined,
        total_alvo: querDesconto ? basePixEnviar : undefined,
        senha_gerencial: senhaGerencial || undefined,
        reaproveitar_cobrancas: reaproveitarIds.length > 0 ? reaproveitarIds : undefined,
        // Valvula da trava de pagamento nao confirmado (admin): finaliza mesmo com Pix/cartao ainda
        // nao caido. So enviado apos o admin confirmar no dialogo abaixo.
        forcar: forcarPagamento || undefined,
      };
      const r = await api.post<{ message: string; venda: { id: number; numero: string; aguardando_nota?: boolean; creditoTrocaInfo?: { consumido: number; saldo_restante: number; troca_id: number | null; troca_numero: string | null } | null } }>(
        `/api/caixa/finalizar/${pedido.id}`,
        payload,
      );
      if (tipoDoc === 'troca') {
        const ci = r.venda.creditoTrocaInfo;
        // Passa o recibo para o pai (CaixaPainel) renderizar o ModalReciboTroca fora do fv-overlay
        // (z-index: 1000) — se renderizado aqui dentro, z-[60] do ModalReciboTroca fica escondido.
        await onSuccess({
          id: r.venda.id,
          numero: r.venda.numero,
          tipo_documento: 'troca',
          recibo: {
            id: ci?.troca_id ?? 0,
            numero: ci?.troca_numero ?? r.venda.numero,
            clienteNome: pedido.cliente_nome,
            clienteCodigo: pedido.cliente_codigo ?? null,
            saldoCredito: ci?.saldo_restante ?? 0,
            creditoNumero: null,
            clienteWhatsapp: null,
          },
        });
        return;
      }
      // Cerco do Pix (DA-248): quando o backend devolve `aguardando_nota`, a venda finalizou SEM nota
      // (Pix/cartao ainda nao confirmado) — a emissao acontece quando a conciliacao bater (aba
      // "Confirmacao Pendente"). O onSuccess do pai NAO dispara a emissao nesse caso.
      await onSuccess({ ...r.venda, tipo_documento: tipoDoc });
    } catch (e: any) {
      // Trava de pagamento eletronico nao confirmado (Ruan 2026-07-14): o Pix/cartao declarado ainda
      // nao caiu. So o admin tem a valvula "Finalizar assim mesmo" (ex.: Pix numa conta que a loja
      // nao captura). Operador comum so ve o aviso e espera o pagamento cair.
      if (e?.corpo?.codigo === 'pagamento_nao_confirmado' && !forcarPagamento) {
        setEnviando(null);
        if (usuario?.perfil === 'admin') {
          const ok = await dialog.confirmar(
            `${e.message}\n\nFinalizar assim mesmo? (a nota so sai quando o pagamento for conciliado)`,
            { titulo: 'Pagamento ainda nao confirmado', perigoso: true, textoConfirmar: 'Finalizar assim mesmo' },
          );
          if (ok) await finalizar(tipoDoc, true);
        } else {
          await dialog.alerta(e.message, { titulo: 'Pagamento ainda nao confirmado', tipo: 'aviso' });
        }
        return;
      }
      // Recusa do servidor (trava anti-nota-furada, estoque, etc): aviso limpo no modal padrao
      // em vez da tarja tecnica com a mensagem crua. As mensagens do backend ja sao amigaveis pt-BR.
      await dialog.alerta(e.message, { titulo: 'Não foi possível finalizar', tipo: 'erro' });
    } finally {
      setEnviando(null);
    }
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--card-bg, #fff)',
    border: '1px solid var(--input-border, #e5e7eb)',
    borderRadius: 8,
    padding: '10px 14px',
  };
  const cardLabelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };
  const cardValueStyle: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 700,
    marginTop: 2,
  };

  // Full-screen takeover: sem o <Modal>, o Esc pra fechar mora aqui.
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  // Lista FIXA dos itens no ledger, entre o cliente e o total (pedido dele, 08/10/2026, com print):
  // os itens ja vem ao abrir o fechamento. Mesmo GET e mesmo cache (`itens`) da Venda CM — que
  // entao nao busca de novo.
  useEffect(() => {
    if (itens === null && !itensCarregando) carregarItens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido.id]);

  function carregarItens() {
    setItensCarregando(true);
    api.get<{ itens?: ItemPedidoVenda[] }>(`/api/caixa/pedidos/${pedido.id}`)
      .then((r) => setItens(r.itens ?? []))
      .catch((e: any) => toast.erro(e.message))
      .finally(() => setItensCarregando(false));
  }

  // ---------------------------------------------------------------------------
  // Venda CM (DA-415, cartao RDS 12.4) — o terceiro desfecho, ao lado das duas notas.
  //
  // Passo 1: garante os itens carregados (a Autorizacao precisa deles) e abre a confirmacao.
  // Os itens vem do mesmo GET que o painel "Ver itens" usa, e ele guarda em `itens` — entao
  // quem ja abriu o painel nao busca de novo.
  function handleVendaCm() {
    if (liberandoVendaCm) return;
    if (itens === null && !itensCarregando) {
      setItensCarregando(true);
      api.get<{ itens?: ItemPedidoVenda[] }>(`/api/caixa/pedidos/${pedido.id}`)
        .then((r) => setItens(r.itens ?? []))
        .catch((e: any) => toast.erro(e.message))
        .finally(() => setItensCarregando(false));
    }
    setModalVendaCm(true);
  }

  // Passo 2: libera de fato. NAO cria venda, NAO emite nota, NAO gera CR/comissao — o pedido
  // troca de tipo, sai da fila do caixa e vai viver na aba "Vendas CM" ate o "Fechar o mes".
  async function confirmarVendaCm() {
    if (liberandoVendaCm) return;
    setLiberandoVendaCm(true);
    try {
      await api.post(`/api/orcamentos/${pedido.id}/venda-cm`, { comprador: retiradoPor.trim() || null });
      setModalVendaCm(false);
      onVendaCm(pedido.numero);
    } catch (e: any) {
      void dialog.alerta(e?.message ?? 'Erro ao liberar como Venda CM.');
    } finally {
      setLiberandoVendaCm(false);
    }
  }

  return (
    <>
    <div className="fv-overlay" role="dialog" aria-modal="true" aria-label={`Finalizar Venda ${pedido.numero}`}>
      {/* -------- LEDGER (esquerda, escuro): identidade + total gigante + desconto + restante -------- */}
      <aside className="fv-ledger">
        <div className="fv-ledger-top">
          {/* Voltar = a mesma saida do X / Cancelar (`onClose`), no molde da seta do ProdutoForm —
              so branca, porque aqui o fundo e o ledger escuro. Trava enquanto finaliza, como o X. */}
          <span className="fv-ledger-id">
            <button
              type="button"
              className="fv-voltar"
              onClick={onClose}
              disabled={!!enviando}
              title="Voltar"
              aria-label="Voltar"
            >
              <ArrowLeft size={20} />
            </button>
            <span className="fv-brand">FIO DE <b>COBRE</b></span>
            {/* Vendedor na linha do titulo: e identidade do pedido (quem atendeu), nao dado do
                cliente — e liberou o bloco de baixo pro cliente + o botao de conferir itens. */}
            <span className="fv-party-sub">{pedido.vendedor_nome || '—'} <b>· Vendedor(a)</b></span>
          </span>
          <span className="fv-pedido">{pedido.numero}</span>
        </div>
        <div className="fv-party">
          <span className="fv-party-label">Cliente</span>
          <span className="fv-party-name">{pedido.cliente_codigo ? `${pedido.cliente_codigo} – ` : ''}{pedido.cliente_nome}</span>
        </div>

        {/* Itens da venda SEMPRE a vista, no espaco entre o cliente e o total (pedido dele,
            08/10/2026): ocupa a sobra do ledger e rola sozinho quando sao muitos — o total
            continua inteiro embaixo. E' a worklist de corte (cabo/mangueira/cano so' se cortam
            depois do pagamento); substituiu o botao "Ver itens" + painel por cima do total. */}
        <div className="fv-itens-inline">
          <div className="fv-itens-colhead">
            <span className="r">#</span><span /><span>Produto</span><span className="r">Qtd</span><span className="r">Valor</span>
          </div>
          {itens === null ? (
            <div className="fv-itens-carregando">{itensCarregando ? 'Carregando…' : 'Nao consegui carregar os itens.'}</div>
          ) : itens.map((it, idx) => (
            // Grid (pedido dele, 08/10/2026): clicar marca a linha de amarelo, ↑/↓ navegam.
            // O "#" e' o numero do item NO ORCAMENTO: as duas rotas ordenam por `oi.ordem`.
            <div key={it.id} className="fv-itens-item" {...gridItens.rowProps(it)}>
              <span className="fv-itens-num">{idx + 1}</span>
              {it.foto_url
                ? <img src={it.foto_url} alt="" className="fv-itens-foto" loading="lazy" />
                : <span className="fv-itens-foto-vazia" aria-hidden="true">📦</span>}
              <span className="fv-itens-desc">{it.produto_descricao || it.codigo_interno || '—'}</span>
              <span className="fv-itens-qtd">
                {Number(it.quantidade).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                {it.unidade_sigla ? <small> {it.unidade_sigla}</small> : null}
              </span>
              <span className="fv-itens-val">{formatMoedaSemSimbolo(Number(it.subtotal), 2)}</span>
            </div>
          ))}
        </div>

        <div className="fv-total-hero">
          {/* Total a ESQUERDA e selo de estado a DIREITA, na mesma linha; o desconto vem embaixo
              (mockups dele, 08/10/2026): libera altura pra lista de itens. */}
          <div className="fv-total-linha">
          <div className="fv-total-main">
          {/* O "R$" mora aqui, na linha do rotulo — o numero embaixo e so o valor. */}
          <div className="fv-total-label">
            {acrescimoCartao > 0.005 ? 'Total a cobrar' : heroMostraFalta ? 'Falta' : 'Total'}
            <span className="fv-total-cur">R$</span>
          </div>
          {temCartao ? (
            <>
              <div className="fv-total-value fv-num">
                <span className="fv-total-big">
                  <TotalPartido valor={totalACobrar} />
                </span>
              </div>
              {acrescimoCartao > 0.005 && (
                <div className="fv-total-note fv-num">
                  venda <b>{formatMoeda(totalPedido)}</b> + cartão <b>{formatMoeda(acrescimoCartao)}</b> <span style={{ opacity: .7 }}>· juros + imposto do parcelamento</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="fv-total-value fv-total-edit" data-no-enter-tab="true">
                {editandoTotal ? (
                  // O onBlur vai NESTE wrapper, nao no MoneyInput: ele espalha {...rest} DEPOIS do
                  // proprio onBlur, entao passar onBlur direto mataria o parse do valor digitado.
                  // `display:contents` some do layout — o input segue filho do flex.
                  <span style={{ display: 'contents' }} onBlur={() => setEditandoTotal(false)}>
                    <MoneyInput className="fv-num" valor={totalPedido} onChangeValor={editarTotalCaixa} autoFocus />
                  </span>
                ) : heroFaltaAtivo ? (
                  // Destaque = o que ainda esta em jogo (DA-561). E TEXTO, nao botao: clicar num
                  // 372,02 e cair num editor de 375,01 seria botao cujo efeito nao bate com o numero
                  // que ele mostra. A edicao do total (DA-202) desce pro subtitulo logo abaixo, que
                  // exibe justamente o numero que ela altera.
                  <span className="fv-total-display fv-num" style={{ cursor: 'default' }}>
                    <TotalPartido valor={diferenca} />
                  </span>
                ) : (
                  // Parado vira TEXTO: so assim reais e centavos tem cor e tamanho proprios (um input
                  // tem cor e fonte unicas). Clicar devolve o MoneyInput, com foco, e a edicao do total
                  // (DA-202) segue igual.
                  <button
                    type="button"
                    className="fv-total-display fv-num"
                    onClick={() => setEditandoTotal(true)}
                    title="Clique pra editar o total da venda"
                  >
                    <TotalPartido valor={totalPedido} />
                  </button>
                )}
              </div>
            </>
          )}
          </div>

          {/* Selo de estado AO LADO do total (DA-403; movido em 08/10/2026, mockup dele): tres
              estados no mesmo ponto, sempre respondendo "falta ou nao falta", colado no numero que
              ele qualifica. Antes vivia logo abaixo do total; o desconto, que estava aqui, desceu. */}
          <div className="fv-selo-estado">
            {/* Hero exibindo a FALTA (DA-561): esta pastilha para de repetir o numero gigante e passa
                a carregar o TOTAL DA VENDA. ⚠️ Nao e' um 5o estado: os quatro selos afirmam SITUACAO
                (falta/aguardando/a receber/quitado) e este e' informacao neutra, entao nao veste o
                laranja de pendencia nem o amarelo do QUITADO (principio 3). E ele herda o clique de
                EDITAR O TOTAL ([DA-202]): a pastilha mostra justamente o numero que a edicao altera. */}
            {heroMostraFalta ? (
              <button
                type="button"
                className="fv-total-venda fv-num"
                onClick={() => setEditandoTotal(true)}
                title="Clique pra editar o total da venda"
              >
                <span className="fv-tv-lbl">TOTAL DA VENDA</span> {formatMoeda(totalPedido)}
              </button>
            ) : diferenca > 0.005 ? (
              <span className="fv-restante-val falta fv-num"><AlertCircle size={20} /> FALTA {formatMoeda(Math.max(0, diferenca))}</span>
            ) : formasAguardando.length > 0 ? (
              // As formas cobrem o total, mas o dinheiro NAO entrou (Pix lancado e nao cobrado, cartao
              // sem aprovar). Antes isso virava QUITADO — a tela dava a venda por paga sem ninguem ter
              // pago nada. Laranja: pendencia normal, nao erro.
              <span className="fv-aguardando"><AlertCircle size={20} /> AGUARDANDO {formasAguardando.join(' + ')}</span>
            ) : cartaoMaquininhaPendente ? null : crediarioTotal > 0.005 ? (
              // Venda fechada, mas (parte do) valor sai fiado: mostra o que a loja tem A RECEBER —
              // no misto, so a parte do crediario (R$100 dinheiro + R$264 fiado => R$264).
              // Duas linhas — "A RECEBER" em cima, o valor embaixo, sem o R$ (pedido dele, 08/10/2026,
              // com print; o "R$" ja esta no rotulo do TOTAL ao lado).
              <span className="fv-areceber"><CalendarClock size={20} /><span className="fv-selo-2l"><span>A RECEBER</span><span className="fv-num">{formatMoedaSemSimbolo(crediarioTotal, 2)}</span></span></span>
            ) : (
              <span className="fv-quitado"><CheckCircle2 size={20} /> QUITADO</span>
            )}
          </div>
          </div>

          {/* Desconto no fechamento (DA-202): dar/retirar ate 5% sem devolver a venda. Desde
              08/10/2026 em linha propria ABAIXO do total, maior (mockup dele). */}
          <div className="fv-money-ctrls" data-no-enter-tab="true">
            {temCartao ? (
              <div className="fv-ctrl-info" style={{ textTransform: 'none' }}>
                {descontoRemovidoPorCartao
                  ? <>Cartão paga cheio na fatia dele — pix mantém o desconto (de {formatMoeda(basePix)} para {formatMoeda(totalPedido)})</>
                  : <>Cartão paga o preço de tabela.</>}
              </div>
            ) : (
              <>
                <div className="fv-ctrl-row">
                  <span className="fv-ctrl-tag"><Tag size={12} /> Desconto</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="fv-num"
                    value={descontoInput}
                    onChange={(e) => setDescontoInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aplicarDescontoCaixa(); } }}
                    placeholder="0,00"
                  />
                  <span style={{ fontWeight: 700 }}>%</span>
                  <button type="button" className="fv-mini-btn pri" onClick={aplicarDescontoCaixa}>Aplicar</button>
                  <button type="button" className="fv-mini-btn sec" onClick={removerDescontoCaixa}>Remover</button>
                </div>
                {descontoAlterado ? (
                  descontoEfetivoPct > 0 ? (
                    <div className="fv-ctrl-info" style={{ textTransform: 'none' }}>{fmtPct(descontoEfetivoPct)}% aplicado · total {formatMoeda(totalPedido)}</div>
                  ) : (
                    <div className="fv-ctrl-info" style={{ textTransform: 'none' }}>sem desconto · total {formatMoeda(totalPedido)}</div>
                  )
                ) : null}
                {/* A linha "Vendedor já aplicou X% (de R$ A para R$ B)" SAIU (pedido dele, 08/10/2026,
                    com print). O % do vendedor continua visivel no proprio campo de desconto. */}
              </>
            )}
          </div>
          {creditoLojaTotal > 0.005 && (
            // O total gigante e o valor da VENDA (52,00), mas o cliente ja pagou parte em credito:
            // o que entra de fato na gaveta / sai na nota e `total - credito`. Esse numero e o que o
            // operador AGE em cima, entao ele sobe de subtitulo miudinho pra linha propria em destaque.
            <div className="fv-total-cobrar fv-num">
              <span className="fv-cobrar-lbl">A cobrar</span>
              <b>{formatMoeda(valorNota)}</b>
              <span className="fv-cobrar-ctx">
                · {semValorACobrar ? 'sem valor a cobrar — não finaliza' : 'crédito vira desconto'}
                {carteiraDaVenda === 'programa' && !semValorACobrar && ' · crédito Parceiro'}
              </span>
            </div>
          )}

          {/* Teto de uso do credito do PROGRAMA (DA-436). Laranja, nao vermelho: e' um limite
              atingido, nao um erro do operador — vermelho aqui e' pro que exige senha gerencial
              (principio 3). Diz o numero que CABE, nao so que passou: a acao e' reduzir pra ele. */}
          {creditoAcimaDoTeto && tetoCreditoPrograma != null && (
            <div className="fv-total-cobrar fv-num" style={{ textTransform: 'none', color: '#f97316' }}>
              <span className="fv-cobrar-lbl">Limite</span>
              <b>{formatMoeda(tetoCreditoPrograma)}</b>
              <span className="fv-cobrar-ctx">
                · o crédito Parceiro cobre até {tetoUsoPct}% da compra; foram lançados
                {' '}{formatMoeda(creditoLojaTotal)} — reduza e complete com outra forma
              </span>
            </div>
          )}

          {/* PROGRAMA DO PRESTADOR (DA-435). Aparece sempre que o pedido tem prestador — inclusive
              quando NAO ha markup, porque "sem premio" e' informacao: se a vendedora esqueceu de
              por o valor informado no Liquido, e' aqui que isso aparece antes de a venda fechar.
              O Custo da Operacao entra como LINHA, nao so o liquido: retencao descoberta depois
              derruba a confianca de um programa que so vive de confianca. */}
          {previaPrograma && (
            <div className="fv-total-cobrar fv-num" style={{ textTransform: 'none' }}>
              <span className="fv-cobrar-lbl">Parceiro</span>
              {previaPrograma.valorCredito > 0.005 ? (
                <>
                  <b>{formatMoeda(previaPrograma.valorCredito)}</b>
                  <span className="fv-cobrar-ctx">
                    · {pedido.prestador_nome} · acréscimo {formatMoeda(previaPrograma.diferenca)} −
                    {' '}Custo da Operação {formatMoeda(previaPrograma.custoOperacao)} ({previaPrograma.custoOperacaoPct}%)
                  </span>
                </>
              ) : (
                <>
                  <b style={{ opacity: 0.6 }}>—</b>
                  <span className="fv-cobrar-ctx">
                    · {pedido.prestador_nome} · sem acréscimo sobre {formatMoeda(previaPrograma.valorOriginal)}: nenhum crédito será gerado
                  </span>
                </>
              )}
            </div>
          )}
        </div>


        {acrescimo > 0.005 && classePag === 'acrescimo' && (
          <div className="fv-acrescimo-note fv-num" style={{ color: '#e0a94a', textTransform: 'none' }}>Acréscimo +{formatMoeda(acrescimo)}</div>
        )}
        {acrescimo > 0.005 && classePag === 'acima_teto' && (
          <div className="fv-acrescimo-note fv-num" style={{ color: '#f87171', textTransform: 'none' }}>+{formatMoeda(acrescimo)} — acima de {TETO_ACRESCIMO_PCT}%</div>
        )}

      </aside>

      {/* -------- WORKSPACE (direita, claro): formas de pagamento + acoes fixas -------- */}
      <section className="fv-work">
        <div className="fv-work-top">
          <span className="fv-work-title">Finalizar venda</span>
          <button type="button" className="btn-fechar-modal" onClick={onClose} aria-label="Fechar" disabled={!!enviando}>×</button>
        </div>

        <div className="fv-scroll">
          <div className="fv-section-label">Formas de pagamento</div>

          {/* [DA-807] Cartao ja aprovado que nenhuma linha assumiu (o pedido veio salvo em Pix e o caixa
              cobrou no cartao, a venda nao fechou e a tela foi reaberta). Sem este aviso o pagamento
              some da tela e ela oferece "Cobrar Pix" — o cliente pagaria duas vezes. Trocar a forma da
              linha pra cartao casa a cobranca sozinho (ORC-008563). */}
          {(() => {
            const soltos = cartoesPagosPedido.filter((r) => {
              const id = r.cobranca_id != null ? Number(r.cobranca_id) : null;
              return id == null || !cartaoPagasConhecidas.includes(id);
            });
            if (soltos.length === 0) return null;
            const desc = soltos.map((r) => `${formatMoeda(Number(r.valor_alocado))}${r.bandeira ? ` · ${r.bandeira}` : ''}`).join(' + ');
            return (
              <div style={{ background: '#f97316', color: '#000', border: '2px solid #c2410c', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontWeight: 600 }}>
                Este pedido já tem cartão APROVADO na maquininha: {desc}. Não cobre de novo — troque a forma
                da linha para Cartão e o pagamento é reconhecido.
              </div>
            );
          })()}

          {pagamentos.map((p, idx) => {
            const fp = getFp(p.forma_pagamento_id);
            const isCrediario = fp?.tipo === 'crediario';
            const isCreditoLoja = fp?.tipo === 'credito_loja';
            const isDinheiro = fp?.tipo === 'dinheiro';
            const isCartaoCredito = fp?.tipo === 'cartao_credito';
            const isCartaoDebito = fp?.tipo === 'cartao_debito';
            const isPix = fp?.tipo === 'pix';
            const exigeCaixa = !isCrediario && !isCreditoLoja;
            const exigeCard = fp?.tipo === 'cartao_credito' || fp?.tipo === 'cartao_debito';
            // Cartão na maquininha integrada (Mercado Pago + Point): a bandeira/NSU/autorização vêm
            // PRONTAS do retorno da maquininha e são gravadas direto na nota — a operadora não digita
            // nada (crédito E débito). Então os campos manuais "Bandeira/Cód. Autorização" só fazem
            // sentido pro cartão roteado pra OUTRA maquininha (PagBank/etc), onde não há integração.
            // Mesma condição que mostra o botão "Cobrar na maquininha" e que o backend usa pra
            // dispensar a digitação (usaPoint) — sem isso o campo vazio confundia (débito "parecia" pedir).
            const naMaquininhaIntegrada = exigeCard && cartaoMaquininha && mercadoPagoCaixaId != null && p.caixa_id === mercadoPagoCaixaId;
            const mostrarCamposCartaoManuais = exigeCard && !naMaquininhaIntegrada;
            // Cobranca ja aprovada nesta linha (detectada ao abrir o modal). No CREDITO o selo mora
            // DENTRO da caixa de parcelas, na linha do botao de simulacao; no DEBITO nao ha caixa de
            // parcelas, entao ele segue na faixa propria abaixo da linha.
            const aprovadoNestaLinha = !!cartaoAprovadoLinha[p.id] && !!cartaoJaPagoLinha[p.id] && !cartaoAtivoLinha[p.id];
            // Linha com cobrança Point ativa: caixa destino travado em Mercado Pago (DA-194).
            const cartaoTravadoMP = (isCartaoCredito || isCartaoDebito) && !!cartaoAtivoLinha[p.id] && !!mercadoPagoCaixaId;
            // Cobrança Pix INLINE (ao lado do Caixa destino): o QR é do Mercado Pago, então só faz
            // sentido quando o destino dessa linha É o Mercado Pago. Em qualquer outro caixa o botão
            // some. Aparece só enquanto não há cobrança em aberto — o QR/aguardando/pago vêm abaixo.
            const caixaEhMercadoPago = mercadoPagoCaixaId != null && p.caixa_id === mercadoPagoCaixaId;
            const pixLinhaAtiva = !!pixAtivoLinha[p.id];
            // Botão "Cobrar Pix" inline (4ª coluna): só no caixa Mercado Pago e enquanto a linha ainda
            // não disparou a cobrança. Ao clicar liga pixAtivoLinha → some o botão e o <CobrancaPix>
            // monta abaixo da linha (a peça cobra ao montar).
            // `!pixJaRecebido`: o Pix desta linha JA caiu e esta vinculado — oferecer "Cobrar Pix" aqui
            // geraria um 2o QR pro cliente pagar de novo (DA-322). O banner abaixo mostra o recebido.
            const pixJaRecebido = !!pixJaPagoLinha[p.id];
            const pixBotaoInline = isPix && pixCobrancaAtiva && p.valor > 0.005 && caixaEhMercadoPago && !pixLinhaAtiva && !pixJaRecebido;
            // Desconto de linha (R$ + %) e o selo "PIX RECEBIDO" moram no FIM desta grade — desconto
            // so em pix/dinheiro (cartao paga cheio, DA-076); selo so quando o Pix ja caiu.
            //
            // As fracoes MUDAM quando o selo entra: elas repartem o espaco que SOBRA, entao um alvo
            // fixo (Forma 153px, Caixa destino 184px) precisa de fracao diferente conforme a linha
            // tem ou nao a coluna de 140px do selo. Sem isso o selo (unica faixa elastica) engolia a
            // folga e truncava o "MERCADO PAGO" — visto no mockup, nao no codigo.
            const temDescontoInline = isPix || isDinheiro;
            const temSeloPix = isPix && pixJaRecebido && !pixLinhaAtiva;
            // ⚠️ As fracoes somam 1 (08/10/2026): antes somavam 0,66-0,95, e fracao com soma < 1 so'
            // reparte ESSA parte da sobra — o resto ficava em branco no fim da linha. Com o painel
            // direito estreitado (ledger 35% -> 41%), a Forma caiu pra 79px e o combo mostrava
            // "CREDI". Mesmas proporcoes entre Forma e Caixa destino, agora usando a largura toda.
            const cols = mostrarCamposCartaoManuais
              // 0.5/0.5 aqui: com Bandeira + Cod. Autorizacao a sobra e' menor, e "CARTAO CREDITO"
              // pede 160px (medido em 1536px de janela: 0.45 dava 155 e cortava o rotulo).
              ? '0.5fr 77px 0.5fr 140px 170px 32px'
              : temDescontoInline
                ? (temSeloPix
                    ? '0.45fr 98px 0.55fr 60px 56px 140px 32px'
                    : isDinheiro
                      ? '0.4fr 98px 0.6fr 60px 56px 104px 104px 32px'
                      : '0.45fr 98px 0.55fr 60px 56px 32px')
                : isCrediario
                  ? '0.45fr 98px 0.55fr 72px 110px 32px'
                  : '0.45fr 98px 0.55fr 32px';
            const labelStyle: React.CSSProperties = {
              fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
              display: 'block', marginBottom: 4, textTransform: 'uppercase',
            };
            return (
              <div key={p.id} className="fv-pay-line">
                <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 10, alignItems: 'flex-end' }}>
                  <div>
                    <label style={labelStyle}>Forma</label>
                    <select
                      value={p.forma_pagamento_id}
                      onChange={(e) => {
                        const novaForma = e.target.value ? Number(e.target.value) : '';
                        // Trocar a forma re-estabelece a linha: zera o desconto e solta o anchor
                        // (o proximo ajuste trava a nova referencia no valor atual).
                        atualizarLinha(idx, { forma_pagamento_id: novaForma, caixa_id: caixaDefaultPara(novaForma), desconto: 0, valorBruto: undefined });
                      }}
                      disabled={pixJaRecebido}
                      title={pixJaRecebido ? TITULO_LINHA_PIX_TRAVADA : undefined}
                      style={{ width: '100%', padding: '6px 8px', fontSize: 14, borderRadius: 4, fontWeight: 600, textTransform: 'uppercase', opacity: pixJaRecebido ? 0.7 : 1, cursor: pixJaRecebido ? 'not-allowed' : 'pointer' }}
                    >
                      {formas.map((f) => <option key={f.id} value={f.id}>{f.nome.toUpperCase()}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Valor</label>
                    <MoneyInput
                      className="cel-num"
                      valor={p.valor}
                      onChangeValor={(v) => editarValorLinha(idx, v)}
                      disabled={pixJaRecebido}
                      title={pixJaRecebido ? TITULO_LINHA_PIX_TRAVADA : undefined}
                      style={{ width: '100%', textAlign: 'right', padding: '6px 8px', fontSize: 14, borderRadius: 4, opacity: pixJaRecebido ? 0.7 : 1, cursor: pixJaRecebido ? 'not-allowed' : 'text' }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>{isCreditoLoja ? 'Qual crédito' : 'Caixa destino'}</label>
                    {exigeCaixa ? (
                      <select
                        value={p.caixa_id}
                        onChange={(e) => atualizarLinha(idx, { caixa_id: e.target.value ? Number(e.target.value) : '' })}
                        disabled={cartaoTravadoMP || pixJaRecebido}
                        title={cartaoTravadoMP ? 'Cobrança na maquininha Mercado Pago — caixa destino fixo' : pixJaRecebido ? TITULO_LINHA_PIX_TRAVADA : undefined}
                        style={{ width: '100%', padding: '6px 8px', fontSize: 14, borderRadius: 4, textTransform: 'uppercase', opacity: (cartaoTravadoMP || pixJaRecebido) ? 0.7 : 1, cursor: (cartaoTravadoMP || pixJaRecebido) ? 'not-allowed' : 'pointer' }}
                      >
                        <option value="">SELECIONE...</option>
                        {caixasParaForma(p.forma_pagamento_id).map((c) => (
                          <option key={c.id} value={c.id}>{c.nome.toUpperCase()}</option>
                        ))}
                      </select>
                    ) : isCreditoLoja ? (
                      // DA-436: uma compra aceita um unico credito, e o operador escolhe qual. O
                      // seletor ocupa o slot do "Caixa destino" (que o credito nao usa) em vez de
                      // abrir coluna nova — a escolha aparece no lugar onde o operador ja procura o
                      // "para onde vai" daquela linha.
                      //
                      // Aparece SEMPRE, mesmo com saldo de um lado so. A 1a versao o escondia
                      // quando nao havia "escolha real", e isso criou um beco: o prestador com
                      // R$ 400 no programa e R$ 0 de troca via a recusa "Trocas tem so R$ 0,00" e
                      // nao tinha como trocar — o campo que consertava estava oculto exatamente no
                      // caso em que era necessario. Com os saldos escritos nas duas opcoes, ele
                      // ainda EXPLICA a recusa em vez de so barrar.
                      <select
                        value={p.credito_origem ?? 'troca'}
                        onChange={(e) => trocarCarteira(e.target.value as CarteiraCredito)}
                        title="Uma compra aceita um crédito de cada vez — escolha de qual carteira sai"
                        style={{ width: '100%', padding: '6px 8px', fontSize: 14, borderRadius: 4, textTransform: 'uppercase', cursor: 'pointer' }}
                      >
                        <option value="troca">TROCAS · {formatMoeda(saldos.troca)}</option>
                        <option value="programa">PARCEIRO · {formatMoeda(saldos.programa)}</option>
                      </select>
                    ) : (
                      // Crediario/Credito da Loja nao tem caixa destino — mas o campo nao some: vira
                      // "editbox travado" (molde do Unitario na CatalogoFornecedor) — moldura de input,
                      // fonte no acento e cursor bloqueado. So-leitura que se ve como campo, alinhado
                      // com os vizinhos, em vez de texto solto flutuando na linha.
                      //
                      // A cor VEM de `--primary` e nao de `--text-color`: a regra !important dos inputs
                      // so alcanca input/select/textarea, entao este <div> cairia no cinza do tema.
                      <div
                        style={{
                          padding: '6px 8px', fontSize: 14, borderRadius: 4, fontWeight: 600,
                          background: 'var(--input-bg)', border: '1px solid var(--input-border, #e5e7eb)',
                          color: 'var(--primary)', cursor: 'not-allowed',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}
                        title="Crediario nao entra em caixa — gera uma conta a receber no nome do cliente"
                      >
                        {/* So o crediario chega aqui: o Credito da Loja passou a usar o slot pro
                            seletor de carteira (DA-436), logo acima. */}
                        GERA CONTA A RECEBER
                      </div>
                    )}
                  </div>
                  {mostrarCamposCartaoManuais && (
                    <>
                      <div>
                        <label style={{ ...labelStyle, color: '#dc2626' }}>Bandeira *</label>
                        <UpperInput
                          value={p.card_bandeira ?? ''}
                          onChange={(v) => atualizarLinha(idx, { card_bandeira: v })}
                          onKeyDown={(e) => {
                            // Enter/Tab: completa com a 1a bandeira que comeca com o digitado
                            if (e.key !== 'Enter' && e.key !== 'Tab') return;
                            const atual = (p.card_bandeira ?? '').toUpperCase().trim();
                            if (!atual) return;
                            const opcoes = ['VISA', 'MASTERCARD', 'ELO', 'HIPERCARD', 'AMERICAN EXPRESS', 'BANESCARD', 'SOROCRED', 'VERO'];
                            if (opcoes.includes(atual)) return;
                            const match = opcoes.find((o) => o.startsWith(atual));
                            if (match) atualizarLinha(idx, { card_bandeira: match });
                          }}
                          placeholder="DIGITE OU SEL..."
                          list="bandeiras-cartao"
                          style={{ width: '100%', padding: '6px 8px', fontSize: 14, borderRadius: 4 }}
                        />
                      </div>
                      <div>
                        <label style={{ ...labelStyle, color: '#dc2626' }}>Cod. Autorizacao *</label>
                        <UpperInput
                          value={p.card_cod_autoriz ?? ''}
                          onChange={(v) => atualizarLinha(idx, { card_cod_autoriz: v })}
                          placeholder="EX: 123456"
                          style={{ width: '100%', padding: '6px 8px', fontSize: 14, borderRadius: 4 }}
                        />
                      </div>
                    </>
                  )}
                  {temDescontoInline && (() => {
                    const ref = refLinha(p);
                    return (
                      <DescontoLinha
                        bruto={ref}
                        desconto={Math.max(0, +(ref - Number(p.valor || 0)).toFixed(2))}
                        onAplicar={(reais) => aplicarDescLinha(idx, reais)}
                        labelStyle={labelStyle}
                      />
                    );
                  })()}
                  {/* Selo do Pix JA RECEBIDO, no fim da propria linha (DA-322): a peca de cobranca NAO
                      monta — cobraria o cliente de novo. Valor e pagador ficam no hover. */}
                  {temSeloPix && (
                    <div
                      title={`${formatMoeda(pixJaPagoLinha[p.id]!.valor)}${pixJaPagoLinha[p.id]!.pagador ? ` · ${pixJaPagoLinha[p.id]!.pagador}` : ''}`}
                      style={{
                        background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 6,
                        padding: '6px 10px', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 6, whiteSpace: 'nowrap',
                        fontSize: 12, fontWeight: 700, color: '#1e3a8a',
                      }}
                    >
                      <CheckCircle2 size={16} />
                      PIX RECEBIDO
                    </div>
                  )}
                  {/* Prazo/Vencimento moram na PROPRIA linha, como o Recebido/Troco do dinheiro. A
                      data e DERIVADA dos dias (ENVIO AO CAIXA + N, piso em hoje) — por isso e
                      caixa de leitura, nao input. Mesma funcao pura que o backend usa pra GRAVAR o
                      titulo, entao a data exibida aqui e exatamente a que vai pro Contas a Receber.
                      🔑 A ancora sai de `ancoraDoVencimento` — a MESMA peca que o backend usa pra
                      gravar ([DA-631]). ⛔ Nao trocar por um `??` escrito aqui: com duas copias,
                      mudar uma so faz a data que a operadora ve deixar de ser a que vai pro titulo,
                      sem erro nenhum. */}
                  {isCrediario && (() => {
                    const dias = p.dias_vencimento ?? 15;
                    const dataVenc = calcularVencimentoCrediario(ancoraDoVencimento(pedido), dias);
                    return (
                      <>
                        <div>
                          <label style={labelStyle}>Vence em</label>
                          <input
                            type="number"
                            className="cel-num"
                            min={1}
                            value={dias}
                            onChange={(e) => atualizarLinha(idx, { dias_vencimento: Number(e.target.value) || 15 })}
                            style={{ width: '100%', textAlign: 'right', padding: '6px 8px', fontSize: 14, borderRadius: 4, fontWeight: 600 }}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Vencimento</label>
                          <div className="cel-num" style={{
                            padding: '6px 8px', fontSize: 14, fontWeight: 700,
                            background: 'var(--input-bg, #f1f5f9)',
                            border: '1px solid var(--input-border, #e5e7eb)',
                            borderRadius: 4, color: 'var(--text-muted)',
                          }}>
                            {formatDataSP(dataVenc)}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                  {/* Recebido/Troco moram na PROPRIA linha (nao num bloco abaixo): o operador le a
                      linha de dinheiro de uma vez so, como ja lia a de Pix. Campos no tamanho da
                      linha (14px) — e o que permite as duas colunas caberem. */}
                  {isDinheiro && (() => {
                    const recebido = p.recebido ?? p.valor;
                    const troco = +(recebido - p.valor).toFixed(2);
                    const temTroco = troco > 0.005;
                    return (
                      <>
                        <div>
                          <label style={labelStyle}>Recebido</label>
                          <MoneyInput
                            className="cel-num"
                            valor={recebido}
                            onChangeValor={(v) => atualizarLinha(idx, { recebido: v })}
                            style={{ width: '100%', textAlign: 'right', padding: '6px 8px', fontSize: 14, borderRadius: 4, fontWeight: 600 }}
                          />
                        </div>
                        <div>
                          <label style={{ ...labelStyle, color: '#7c3aed' }}>Troco</label>
                          <div className="cel-num" style={{
                            padding: '6px 8px', fontSize: 14, fontWeight: 700,
                            background: temTroco ? '#ede9fe' : 'var(--input-bg, #f1f5f9)',
                            border: `1px solid ${temTroco ? '#ddd6fe' : 'var(--input-border, #e5e7eb)'}`,
                            borderRadius: 4,
                            color: temTroco ? '#7c3aed' : 'var(--text-muted)',
                          }}>
                            {formatMoeda(Math.max(0, troco))}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                  <div>
                    {/* Linha de Pix JA RECEBIDO nao se remove aqui ([DA-625]): o dinheiro entrou e
                        esta amarrado ao pedido em `mp_vinculos`. Tirar a linha nao devolve o Pix —
                        so faz a venda parar de declarar o que a loja recebeu. Quem precisa desfazer
                        usa a Conciliacao Bancaria, que e onde o vinculo mora ([DA-560]). */}
                    {pagamentos.length > 1 && !pixJaRecebido && (
                      <button
                        type="button"
                        onClick={() => removerLinha(idx)}
                        title="Remover"
                        style={{
                          background: 'transparent', border: 0, color: '#dc2626',
                          cursor: 'pointer', padding: 6, borderRadius: 4,
                        }}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>
                {pixBotaoInline && (
                  <div className="fv-pl-actions">
                    <button type="button" className="fv-cobrar-btn" onClick={() => setPixAtivoLinha((m) => ({ ...m, [p.id]: true }))}>
                      <QrCode size={16} />
                      Cobrar Pix ({formatMoeda(p.valor)})
                    </button>
                  </div>
                )}
                {isCartaoCredito && (() => {
                  const est = parcelamentos[p.id];
                  const planos = est?.data?.parcelas ?? [];
                  const nSel = p.card_parcelas ?? 1;
                  const sel = planos.find((x) => x.n === nSel);
                  const semJurosAte = est?.data?.sem_juros_ate ?? 1;
                  return (
                    // Sem `maxWidth`: a caixa ocupa a largura da linha de pagamento. Ela agora abriga
                    // tambem o selo de APROVADO (antes numa faixa solta abaixo) — apertada em 520px
                    // o selo e o botao do WhatsApp nao caberiam lado a lado.
                    <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 6, background: 'var(--input-bg, #f1f5f9)', border: '1px solid var(--input-border, #e5e7eb)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 12, alignItems: 'flex-end' }}>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>
                            Parcelas
                          </label>
                          <select
                            value={nSel}
                            disabled={!est?.data}
                            onChange={(e) => {
                              const n = Number(e.target.value);
                              atualizarLinha(idx, { card_parcelas: n });
                              avisarParceladoPeloCliente(p.id, n);
                            }}
                            style={{ width: '100%', padding: '6px 8px', fontSize: 14, borderRadius: 4, fontWeight: 600 }}
                          >
                            {planos.length === 0
                              ? <option value={1}>1X</option>
                              : planos.map((x) => (
                                  <option key={x.n} value={x.n}>
                                    {x.n}X DE {formatMoeda(x.valor_parcela / 100)}{x.sem_juros ? ' SEM JUROS' : x.juros_mes ? ` · ${x.juros_mes.toFixed(1).replace('.', ',')}% a.m.` : ''}
                                  </option>
                                ))}
                          </select>
                        </div>
                        <div>
                          {est?.loading && (
                            <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Loader2 size={14} className="animate-spin" /> Consultando taxas da PagBank…
                            </span>
                          )}
                          {est?.erro && !est.loading && (
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#92400e' }}>
                              Parcelamento indisponivel — cobre 1x no preco de tabela.
                            </span>
                          )}
                          {sel && !est?.loading && !est?.erro && (
                            <div>
                              <div style={{ fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Cobrar na maquininha</div>
                              <div className="cel-num" style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-color)' }}>
                                {formatMoeda(sel.total / 100)}
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginLeft: 8 }}>
                                  {sel.n}x de {formatMoeda(sel.valor_parcela / 100)}
                                </span>
                              </div>
                              {!!sel.imposto && sel.imposto > 0 && (
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                                  inclui {formatMoeda(sel.imposto / 100)} de imposto
                                </div>
                              )}
                              {!!sel.juros_mes && sel.juros_mes > 0 && (
                                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)', marginTop: 2 }}>
                                  Juros do parcelamento: {sel.juros_mes.toFixed(1).replace('.', ',')}% a.m.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {est?.data && !est.loading && !est.erro && (
                        <div style={{ marginTop: 8, display: 'inline-block', background: '#fde68a', color: '#000', fontWeight: 700, fontSize: 12, padding: '3px 10px', borderRadius: 999 }}>
                          {semJurosAte > 1 ? `Sem juros ate ${semJurosAte}x` : 'Juros a partir do 2x'}
                          {sel && sel.n > semJurosAte ? ' · esta opcao tem juros' : ''}
                        </div>
                      )}
                      {/* Rodape da caixa: o gatilho da simulacao e, A FRENTE dele, o recebimento ja
                          aprovado na maquininha — que antes ocupava uma faixa inteira logo abaixo,
                          repetindo o valor que esta bem ali em cima. Lado a lado, "quanto cobrar" e
                          "ja foi cobrado" se leem juntos. */}
                      {(est?.data && !est.loading && !est.erro) || aprovadoNestaLinha || !cartaoAtivoLinha[p.id] ? (
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          {est?.data && !est.loading && !est.erro && (
                            <SimulacaoParcelamentoWhatsApp
                              parcelas={est.data.parcelas}
                              provedor={parcelamentoProvedor[p.id] ?? 'Mercado Pago'}
                              orcamentoId={pedido.id}
                              numero={pedido.numero}
                              clienteId={pedido.cliente_id}
                              clienteNome={pedido.cliente_nome}
                              operador={usuario?.nome}
                              onErro={toast.erro}
                            />
                          )}
                          {aprovadoNestaLinha ? (
                            <div style={{
                              background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 6,
                              padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8,
                              fontSize: 14, fontWeight: 700, color: '#1e3a8a', flex: '1 1 auto',
                            }}>
                              <CheckCircle2 size={18} style={{ flex: '0 0 auto' }} />
                              APROVADO NA MAQUININHA — {formatMoeda(cartaoJaPagoLinha[p.id]!.valor)}
                              {cartaoJaPagoLinha[p.id]!.bandeira ? ` · ${cartaoJaPagoLinha[p.id]!.bandeira}` : ''}
                              {(cartaoJaPagoLinha[p.id]!.parcelas ?? 1) > 1 ? ` · ${cartaoJaPagoLinha[p.id]!.parcelas}x` : ''}
                            </div>
                          ) : !cartaoAtivoLinha[p.id] ? (
                            // Na linha da Point, "AGUARDANDO CARTAO" lia como "ja esta na maquininha" —
                            // e nada tinha sido enviado (10/10/2026, relato do balcao). Ali o texto diz
                            // o que FALTA fazer; nas outras contas (sem o botao) segue o rotulo antigo.
                            <span className="fv-aguardando" style={{ flex: '1 1 auto', justifyContent: 'center', fontSize: 14 }}>
                              <AlertCircle size={16} />
                              {cartaoMaquininha && mercadoPagoCaixaId != null && p.caixa_id === mercadoPagoCaixaId
                                ? 'FALTA COBRAR — clique em Cobrar na maquininha'
                                : `AGUARDANDO ${(getFp(p.forma_pagamento_id)?.nome ?? 'CARTÃO').toUpperCase()}`}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
                {/* Cartão na maquininha Mercado Pago Point (modo PDV) pela PEÇA ÚNICA <CobrancaCartao> (016):
                    o gatilho "Cobrar na maquininha" liga cartaoAtivoLinha[id] → a peça monta, COBRA AO
                    MONTAR e conduz o ciclo (manda o valor → aguarda na maquininha → aprova sozinho / tenta
                    de novo). A venda só finaliza quando a peça avisa onAprovado (trava anti-erro). Sem QR —
                    o cliente mexe na maquininha. SÓ no caixa Mercado Pago (a Point é exclusiva do MP);
                    cartão roteado pra PagBank/outro usa o fluxo manual (bandeira + autorização) — DA-219. */}
                {(isCartaoCredito || isCartaoDebito) && cartaoMaquininha && mercadoPagoCaixaId != null
                  && p.caixa_id === mercadoPagoCaixaId && p.valor > 0.005 && (
                  cartaoAtivoLinha[p.id] ? (() => {
                    // Total a EXIBIR no aprovado: parcelado = preço de cartão + juros (não o p.valor, que é
                    // só o principal). No 1x / débito são iguais. A nota sai pelo valor real no backend.
                    const estA = parcelamentos[p.id];
                    const nParc = isCartaoCredito ? (p.card_parcelas ?? 1) : 1;
                    const selA = estA?.data?.parcelas.find((x) => x.n === nParc);
                    const totalCobrado = nParc > 1 && selA ? selA.total / 100 : p.valor;
                    // O que sobe pro device = principal + imposto (mesma conta do /cartao/cobrar, que
                    // chama o mesmo calcularParcelamentoMP). É o que a tela da senha mostra (DA-808).
                    const valorNaSenha = nParc > 1 && selA ? p.valor + (selA.imposto ?? 0) / 100 : undefined;
                    return (
                      <div style={{ marginTop: 10 }}>
                        <CobrancaCartao
                          alvo={{ tipo: 'venda', orcamentoId: pedido.id }}
                          valor={p.valor}
                          cardType={isCartaoDebito ? 'debit' : 'credit'}
                          parcelas={nParc}
                          layout="inline"
                          totalAprovado={totalCobrado}
                          valorNaSenha={valorNaSenha}
                          onAprovado={(_rec, cobId) => {
                            if (cobId != null) cartaoPagasSessaoRef.current.add(cobId);
                            setCartaoAprovadoLinha((m) => ({ ...m, [p.id]: true }));
                          }}
                          onCancelado={() => resetCartaoLinha(p.id)}
                          pagasConhecidas={cartaoPagasConhecidas}
                          imprimirVia={imprimirViaCartao}
                        />
                      </div>
                    );
                  })() : (cartaoAprovadoLinha[p.id] && cartaoJaPagoLinha[p.id]) ? (
                    // Cobranca JA PAGA detectada ao abrir: mostra APROVADO e libera a nota, sem o
                    // botao "Cobrar na maquininha" (que cobraria o cliente de novo). O backend
                    // finaliza lendo essa cobranca paga (usaPoint por orcamento_id).
                    <div style={{ marginTop: 10 }}>
                      {/* Credito ja mostrou o selo dentro da caixa de parcelas — repetir aqui seria
                          a mesma frase duas vezes na tela. Debito nao tem aquela caixa, entao e aqui
                          que o selo aparece. */}
                      {isCartaoDebito && (
                        <div style={{
                          background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 6,
                          padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
                          fontSize: 14, fontWeight: 700, color: '#1e3a8a',
                        }}>
                          <CheckCircle2 size={18} />
                          APROVADO NA MAQUININHA — {formatMoeda(cartaoJaPagoLinha[p.id]!.valor)}
                          {cartaoJaPagoLinha[p.id]!.bandeira ? ` · ${cartaoJaPagoLinha[p.id]!.bandeira}` : ''}
                          {(cartaoJaPagoLinha[p.id]!.parcelas ?? 1) > 1 ? ` · ${cartaoJaPagoLinha[p.id]!.parcelas}x` : ''}
                        </div>
                      )}
                      {/* A tarja da diferenca RESOLVIDA saiu daqui (DA-403): ela fala do pagamento que
                          cobriu o buraco, entao mora embaixo DAQUELA linha — ver `tarjaDiferenca`. */}
                      {cartaoJaPagoLinha[p.id]!.ajustadaDe == null && (
                        <div style={{ fontSize: 12, color: '#4b5563', marginTop: 4 }}>
                          Cobrança já recebida e conciliada — o "Finalizar" usa ela, não cobra de novo.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="fv-pl-actions" style={{ marginTop: 10, alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                      <ImprimirViaCartao marcado={imprimirViaCartao} onChange={setImprimirViaCartao} />
                      <button
                        type="button"
                        className="fv-cobrar-btn"
                        onClick={() => iniciarCartao(p.id)}
                      >
                        <CreditCard size={16} />
                        {isCartaoCredito && (p.card_parcelas ?? 1) > 1
                          ? `Cobrar na maquininha (${p.card_parcelas}x)`
                          : `Cobrar na maquininha (${formatMoeda(p.valor)})`}
                      </button>
                    </div>
                  )
                )}
                {/* Pix por linha (DA-237 Fase 2): a peça única <CobrancaPix> faz o CICLO da cobrança
                    dessa linha — cobra ao montar, mostra o QR na tela do cliente, confirma sozinho
                    (número 62-05 ou valor quebrado único) ou pede o pagador, imprime comprovante, e
                    cancela no desmonte. layout 'inline' (QR 64px ao lado). O gatilho "Cobrar Pix" liga
                    o flag; o "pago" só marca estado — a venda finaliza pelos botões Nota. */}
                {isPix && pixCobrancaAtiva && p.valor > 0.005 && pixAtivoLinha[p.id] && (
                  <div style={{ marginTop: 10 }}>
                    <CobrancaPix
                      alvo={{
                        tipo: 'venda',
                        orcamentoId: pedido.id,
                        // cobre_total: a linha paga o total da venda → o backend calibra o valor pra
                        // centavo único (DA-234), pra confirmar sozinho. Pix parcial não calibra.
                        cobreTotal: Math.abs(p.valor - totalPedido) < 0.005,
                        cpf: cpfNota.replace(/\D/g, '') || undefined,
                      }}
                      valor={p.valor}
                      layout="inline"
                      // Comprovante só quando o destino é o Mercado Pago (conta do QR estático).
                      mostrarComprovante={caixaEhMercadoPago}
                      // O backend subiu uns centavos pra confirmar sozinho — reflete na linha + total.
                      // ⚠️ [DA-628] O total sobe pelo DELTA, nunca recebe o valor da linha. Enquanto só
                      // o QR que cobria o total calibrava, `setTotalAlvo(novoValor)` acertava por
                      // coincidência (ali a linha É o total); num PARCIAL ele jogaria o total da venda
                      // de R$ 102,00 pra R$ 2,00. Pelo delta os dois casos caem na mesma conta certa.
                      // O alvo mora no nível `basePix` (antes da regra do cartão e do desconto de
                      // linha), que é onde `totalAlvo` é lido — somar sobre `totalPedido` desconseria
                      // `descontoLinhas` duas vezes.
                      onValorAjustado={(novoValor) => {
                        const valorAntes = p.valor;
                        const delta = Math.round((novoValor - valorAntes) * 100) / 100;
                        const totalAlvoDepois = Math.round((basePix + delta) * 100) / 100;
                        setAjusteQrPorLinha((m) => (m[p.id] ? m : {
                          ...m, [p.id]: { valorAntes, delta, totalAlvoAntes: totalAlvo, totalAlvoDepois },
                        }));
                        setPagamentos((ps) => ps.map((x) => (x.id === p.id ? { ...x, valor: novoValor } : x)));
                        setTotalAlvo(totalAlvoDepois);
                        toast.sucesso(`Valor ajustado pra ${formatMoeda(novoValor)} — centavo único do Pix (confirma sozinho)`);
                      }}
                      // O Pix CAIU: marca a linha como dinheiro que entrou de verdade. Sem isto a
                      // tarja da diferença tratava a linha lançada como se já estivesse paga (DA-403).
                      onPago={() => setPixPagoNaSessao((m) => ({ ...m, [p.id]: true }))}
                      // Cancelada (operador / outro ponto / 409 ao montar) → some o QR, volta o botão.
                      // [DA-628] E o centavo do QR parcial vai junto: ninguém pagou por ele.
                      onCancelado={() => {
                        desligarPixLinha(p.id);
                        if (!pixPagoNaSessao[p.id]) desfazerAjusteQr(p.id, true);
                      }}
                    />
                  </div>
                )}
                {/* Diferenca do cartao RESOLVIDA — a tarja mora sob a linha que a cobriu (DA-403). */}
                {tarjaDiferenca?.host === p.id && (
                  <div style={{
                    background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 6,
                    padding: '9px 12px', marginTop: 10, fontSize: 13, color: '#1e3a8a',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <CheckCircle2 size={18} style={{ flex: '0 0 auto' }} />
                    <span style={{ fontWeight: 800, letterSpacing: '.03em', flex: '0 0 auto' }}>PEDIDO QUITADO</span>
                    <span style={{ fontWeight: 500 }}>
                      — a diferença de {formatMoeda(tarjaDiferenca.gap)} foi resolvida
                      {tarjaDiferenca.partes.length > 0 ? `: ${tarjaDiferenca.partes.join(' + ')}` : ''}. O cartão não é cobrado de novo.
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={adicionarLinha}
            disabled={diferenca <= 0.005}
            title={diferenca <= 0.005
              ? 'Recebimento já coberto. Pra dividir, baixe o Valor de uma linha — o que faltar acende este botão.'
              : `Falta ${formatMoeda(diferenca)} — lance aqui a outra forma que o cliente vai pagar.`}
            className="fv-add-forma"
          >
            <Plus size={16} /> Adicionar forma de pagamento
          </button>

          {/* CPF so vale pro cupom: na A4 o CPF sai do cadastro do cliente (pedido dele, DA-877). */}
          {tipoNota === 'nfce' && (
            <div className="fv-cpf">
              <label>CPF na nota (opcional)</label>
              <input
                value={cpfNota}
                onChange={(e) => setCpfNota(e.target.value)}
                placeholder="000.000.000-00"
              />
            </div>
          )}

          <div className="fv-cpf">
            <label>Tipo de Nota</label>
            <select
              value={tipoNota}
              onChange={(e) => setTipoNota(e.target.value as 'nfce' | 'nfe')}
              disabled={!!enviando || !!pedido.cliente_travar_nota_a4}
            >
              <option value="nfce" disabled={!!pedido.cliente_travar_nota_a4}>Consumidor</option>
              <option value="nfe" disabled={nfeIndisponivel}>
                {nfeIndisponivel ? 'A4 (precisa de cliente cadastrado)' : 'A4'}
              </option>
            </select>
          </div>

          {nfeIndisponivel && (
            <div className="fv-banner erro">
              Nota A4 indisponível — Consumidor genérico — selecione um cliente cadastrado
            </div>
          )}
          {crediarioBloqueado && (
            <div className="fv-banner alerta">
              Operação permitida apenas para Gerentes
            </div>
          )}
        </div>{/* fv-scroll */}

        <div className="fv-actions">
          {/* Trava do cadastro (migration 0263): quando o Consumidor some por causa DELA, o motivo
              nao pode ficar so no cadastro do cliente — quem esta no balcao ve um botao so e
              precisa saber por que (principio 1: a tela nunca esconde o estado do operador). */}
          {pedido.cliente_travar_nota_a4 && (
            <span className="fv-actions-hint">
              {/* Sem o nome do cliente (pedido dele, 08/10/2026): ele ja aparece no painel preto. */}
              Cliente so recebe <b>Nota A4</b> — travado no cadastro do cliente
            </span>
          )}
          {!pedido.cliente_travar_nota_a4 && pedido.tipo_documento && (
            <span className="fv-actions-hint">
              {/* DA-877: so informa — a troca mora na caixa "Tipo de Nota". */}
              Vendedora marcou: <b>{nomeTipoNota(pedido.tipo_documento)}</b>
            </span>
          )}
          <button type="button" className="btn-secundario" onClick={onClose} disabled={!!enviando}>
            Cancelar
          </button>
          {(
            <>
              {/* DA-877: UM botao so, que finaliza no tipo da caixa "Tipo de Nota" e diz qual e'.
                  (Na DA-432 eram dois botoes gemeos lado a lado; o clique distraido no vizinho
                  emitia a nota errada.) */}
              {(() => {
                const bloqueado = !!enviando || finalizarBloqueado || (tipoNota === 'nfe' && nfeIndisponivel);
                return (
              <button
                type="button"
                onClick={() => finalizar(tipoNota)}
                disabled={bloqueado}
                style={{
                  background: '#2563eb', color: '#fff', border: 0,
                  padding: '12px 22px', borderRadius: 12, fontWeight: 700,
                  cursor: bloqueado ? 'not-allowed' : 'pointer',
                  opacity: bloqueado ? 0.5 : 1,
                  display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 15,
                }}
                title={crediarioBloqueado ? 'Operação permitida apenas para Gerentes' : (tipoNota === 'nfe' && nfeIndisponivel) ? 'NF-e exige cliente cadastrado' : classePag === 'abaixo' ? `Falta ${formatMoeda(diferenca)} pra fechar a venda — lance a forma que o cliente vai pagar` : cartaoBloqueiaFinalizar ? 'Cobre o cartão na maquininha primeiro — a venda só finaliza quando a maquininha APROVAR' : pixBloqueiaFinalizar ? 'O Pix ainda não caiu — cobre o Pix, ou amarre o recebimento na Conciliação Bancária (admin pode usar o "Validar")' : semValorACobrar ? 'Venda sem valor a cobrar — o crédito cobre o total e zeraria a nota' : creditoAcimaDoTeto ? `O crédito Parceiro cobre até ${tetoUsoPct}% da compra — reduza o valor lançado` : ''}
              >
                {enviando === tipoNota
                  ? <Loader2 size={16} className="animate-spin" />
                  : tipoNota === 'nfe' ? <FileText size={16} /> : <Receipt size={16} />}
                {nomeTipoNota(tipoNota)}
              </button>
                );
              })()}
              {/* Terceiro desfecho (DA-415): nao e "como se paga", e "que documento sai" — e a
                  resposta aqui e "nenhum agora, a nota sai no fechamento do mes". Laranja porque
                  no sistema ele significa pendente/atencao, que e exatamente o estado em que o
                  pedido fica. Nao herda `finalizarBloqueado`: aquilo trava por REGRA DE COBRANCA
                  (cartao nao aprovado, credito cobrindo o total, crediario sem permissao) e a
                  Venda CM nao cobra nada. */}
              {podeVendaCm && (
                <button
                  type="button"
                  onClick={handleVendaCm}
                  disabled={!!enviando || liberandoVendaCm}
                  style={{
                    background: '#f97316', color: '#000', border: 0,
                    padding: '12px 22px', borderRadius: 12, fontWeight: 700,
                    cursor: enviando || liberandoVendaCm ? 'not-allowed' : 'pointer',
                    opacity: enviando || liberandoVendaCm ? 0.5 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 15,
                  }}
                  title="Libera o material sem nota — a nota e a cobranca saem no Fechar o mes"
                >
                  {liberandoVendaCm
                    ? <Loader2 size={16} className="animate-spin" />
                    : <PackageCheck size={16} />}
                  Venda CM
                </button>
              )}
            </>
          )}
        </div>
        {versao && (
          <div
            title={detalheVersao(versao)}
            style={{
              textAlign: 'right', fontSize: 11, letterSpacing: '0.02em',
              color: 'var(--text-muted, #9ca3af)', paddingTop: 6,
            }}
          >
            FIODECOBRE <b style={{ color: 'var(--primary)' }}>{versao.versao}</b>
          </div>
        )}
      </section>
    </div>

    <datalist id="bandeiras-cartao">
      <option value="VISA" />
      <option value="MASTERCARD" />
      <option value="ELO" />
      <option value="HIPERCARD" />
      <option value="AMERICAN EXPRESS" />
      <option value="BANESCARD" />
      <option value="SOROCRED" />
      <option value="VERO" />
    </datalist>

    {/* Confirmacao da Venda CM (DA-415). Modal proprio, e nao o `dialog.confirmar()` global: aquele
        tem 2 botoes fixos e aqui sao 3, com a Autorizacao no meio (molde DA-377). O aviso do
        descarte e o que impede o pecado da DA-408 — o operador pode ter preenchido pix/dinheiro
        antes de decidir pela Venda CM, e jogar isso fora CALADO seria o defeito; avisado, nao e. */}
    <Modal
      aberto={modalVendaCm}
      onFechar={() => setModalVendaCm(false)}
      titulo="Venda CM — liberar sem nota"
      largura={600}
      compacto
      rodape={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, width: '100%' }}>
          <button
            type="button"
            onClick={() => setModalVendaCm(false)}
            disabled={liberandoVendaCm}
            style={{ background: '#e5e7eb', color: '#1f2937', border: 0, padding: '8px 24px', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
          >Cancelar</button>
          <BotaoAutorizacao
            variante="header"
            cor="laranja"
            numero={pedido.numero}
            clienteNome={pedido.cliente_nome}
            clienteDoc={pedido.cliente_doc}
            comprador={retiradoPor}
            itens={(itens ?? []).map((it) => ({
              codigo: it.codigo_interno,
              descricao: it.produto_descricao ?? '',
              quantidade: Number(it.quantidade),
              unidade: it.unidade_sigla,
              preco_unitario: Number(it.preco_unitario),
              subtotal: Number(it.subtotal),
            }))}
            total={pedido.total}
            formaPagamento="Venda CM (sem nota)"
          />
          <button
            type="button"
            onClick={confirmarVendaCm}
            disabled={liberandoVendaCm}
            // AZUL, nao laranja: a Autorizacao ao lado ja e laranja, e dois botoes da mesma cor
            // colados viram um borrao — a separacao entre "imprimir o comprovante" e "liberar de
            // verdade" e o que essa cor carrega (mesmo molde do DA-377/DA-381).
            style={{
              background: '#2563eb', color: '#fff', border: 0, padding: '8px 24px', borderRadius: 6,
              fontWeight: 700, cursor: liberandoVendaCm ? 'not-allowed' : 'pointer', fontSize: 14,
              opacity: liberandoVendaCm ? 0.5 : 1,
              display: 'inline-flex', alignItems: 'center', gap: 7,
            }}
          >
            {liberandoVendaCm && <Loader2 size={15} className="animate-spin" />}
            Sim, liberar
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0', fontSize: 14, lineHeight: 1.5 }}>
        <div>
          O material de <strong>{pedido.cliente_nome}</strong> sai agora <strong>sem nota fiscal</strong>.
          A nota e a cobrança saem no <strong>Fechar o mês</strong>. Até lá o cliente pode devolver o que não usar.
        </div>
        <div
          style={{
            background: '#f97316', color: '#000', borderRadius: 8, padding: '10px 12px',
            fontWeight: 600, display: 'flex', alignItems: 'flex-start', gap: 8,
          }}
        >
          <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Venda CM não se combina com pagamento. O que estiver preenchido em
            {' '}<strong>Formas de pagamento</strong> e o desconto digitado aqui serão descartados —
            vale o pedido como está salvo.
          </span>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--text-muted)' }}>
            Retirado por
          </span>
          <UpperInput
            value={retiradoPor}
            onChange={setRetiradoPor}
            placeholder="QUEM ESTA LEVANDO O MATERIAL"
            disabled={liberandoVendaCm}
          />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Necessário para imprimir a Autorização (o comprovante que quem retira assina).
          </span>
        </label>
      </div>
    </Modal>

    {showSenhaDesc && (
      <ModalSenhaGerencial
        onConfirm={(senha) => {
          setSenhaGerencial(senha ?? '');
          setShowSenhaDesc(false);
          const t = pendenteAplicar;
          setPendenteAplicar(null);
          if (t != null) {
            const pctEff = cheio > 0 ? (1 - t / cheio) * 100 : 0;
            setTotalAlvo(t);
            setDescontoInput(pctEff > 0.005 ? fmtPct(Math.round(pctEff * 100) / 100) : '');
            refitPagamentos(t);
          }
          // Desconto por linha (pix/dinheiro) que aguardava a senha: aplica agora com a senha valida.
          const dl = pendenteDescLinha;
          setPendenteDescLinha(null);
          if (dl != null) {
            const linha = pagamentos[dl.idx];
            if (linha) {
              // Mesma regra pura do caminho sem senha — senao o desconto autorizado poderia cair
              // num numero diferente do que a tela pediu autorizacao pra aplicar.
              const novo = editarDescontoDaLinha(refLinha(linha), dl.desconto);
              setPagamentos((arr) => arr.map((p, i) => (i === dl.idx ? { ...p, valor: novo.valor, desconto: novo.desconto, valorBruto: novo.bruto } : p)));
              desligarPixLinha(linha.id);
            }
          }
        }}
        onCancel={() => { setShowSenhaDesc(false); setPendenteAplicar(null); setPendenteDescLinha(null); }}
      />
    )}
    </>
  );
}
